// devBackendShim — DATA-LAYER ONLY (no UI). Lets the REAL beta web app run against the
// NEW dev backend (/api/v1) at app-dev.coowe.com without touching a single screen/component.
//
// The beta FE speaks the beta REST contract (/users/teeups, /teeups/{id}, ...) and expects the
// raw beta backend shapes that the Redux formatters (formatTeeup, formatTeeupUsers, ...) consume.
// The dev backend speaks /api/v1 with different paths + shapes and dev-mode auth (token as a
// ?token= QUERY PARAM, not a Bearer header). This module installs axios interceptors on the
// api.client instance that (1) rewrite known beta paths -> dev paths and attach ?token=, and
// (2) reshape dev responses back into the beta shapes. Unknown endpoints fall through to a safe
// empty shape so the app never hard-crashes on a dev gap.
//
// Mirrors the proven mobile approach (teeup-mobile backend shim). Enabled only when the API base
// is same-origin "/api" (i.e. the dev deploy); a no-op otherwise so prod builds are untouched.

import { confirmAlert } from "react-confirm-alert"

import I18n from "@i18n"
import { getItem } from "@utils/localStorage"

// FIX 2026-07-04: this module is data-layer-only by design (see header comment), but a blocked
// stage-change response (backend change_stage 400, e.g. Ended/Cancelled are terminal per
// lifecycle.py:19) used to be swallowed as a console.warn -- from the user's side, a "revert"
// confirmed then silently did nothing. confirmAlert (react-confirm-alert) is already the app's
// real, visible alert mechanism (DropDown/index.tsx uses it for the same kind of blocked/confirm
// messaging); @ui/toast's Toast.show is a no-op stub on web (RootSiblings render is commented out,
// it never shows anything), so it cannot be used to surface this.

const API_BASE = process.env.REACT_APP_API_ENDPOINT // "/api" on the dev deploy

export const isDevBackend = () => API_BASE === "/api"

const getToken = () => {
  try {
    return getItem("userInfo")?.accessToken || null
  } catch {
    return null
  }
}

// ---- id aliasing: beta FE assumes INTEGER ids and does Number(id) everywhere; dev uses UUIDs
// (Number(uuid) = NaN, which silently breaks per-teeup/per-user keying). Assign each UUID a stable
// integer alias the FE sees, and reverse-map int -> uuid on outbound requests. Persisted to
// localStorage so aliases survive reloads (the inbox list populates them before any detail open).
const ALIAS_KEY = "coowe.devIdAlias"
let intToUuid = {}
let uuidToInt = {}
let aliasCounter = 100001
const loadAlias = () => {
  try {
    const m = JSON.parse(window.localStorage.getItem(ALIAS_KEY) || "{}")
    intToUuid = m.i || {}
    uuidToInt = m.u || {}
    aliasCounter = m.c || 100001
  } catch {
    /* ignore */
  }
}
const saveAlias = () => {
  try {
    window.localStorage.setItem(
      ALIAS_KEY,
      JSON.stringify({ i: intToUuid, u: uuidToInt, c: aliasCounter })
    )
  } catch {
    /* ignore */
  }
}
loadAlias()

// uuid -> stable int (numbers/falsy pass through)
export const aliasId = uuid => {
  if (uuid == null || typeof uuid === "number") return uuid
  const s = String(uuid)
  if (!/[a-f-]/i.test(s) && /^\d+$/.test(s)) return Number(s) // already numeric
  if (uuidToInt[s]) return uuidToInt[s]
  const n = aliasCounter++
  uuidToInt[s] = n
  intToUuid[n] = s
  saveAlias()
  return n
}
// int (or int-string) -> uuid (pass through if already a uuid / unknown)
export const unaliasId = v => {
  if (v == null) return v
  const s = String(v)
  return intToUuid[s] || v
}

// ---- enum maps (raw beta codes the FE formatters expect) ----
const TEEUP_STATUS = {
  planning: 1,
  allset: 2,
  all_set: 2, // backend emits snake_case; without this all_set fell to 1 (Planning) -> web/mobile parity bug
  // BUG-036: "scheduled" (When decided, Where NOT decided) is a DISTINCT stage from all_set
  // (When AND Where decided). Map it to code 7 so the FE renders "Scheduled" (not "All Set").
  // Android uses the same numeric-band trick but adds an isScheduled flag; the web uses code 7
  // + getStageOptionsByStatusId()[7] to produce the distinct badge.
  scheduled: 7,
  happening: 3,
  ended: 4,
  cancelled: 5,
  happeningSoon: 6,
  happening_soon: 6,
}
const USER_STATUS = {
  invited: 1,
  going: 2,
  might_go: 3,
  mightgo: 3,
  not_going: 4,
  notgoing: 4,
  joined: 5,
  on_my_way: 6,
  arrived: 7,
}

const teeupStatusCode = s => TEEUP_STATUS[s] || 1
const userStatusCode = s => (s ? USER_STATUS[s] || 2 : undefined)

// ARCHITECTURE RULE (QJ, #150/S3-loss fix): NOTHING points to AWS, EVER. The original media lived
// in the `cooe-users-prod` S3 bucket whose objects were lost (every key 404s); the files survive on
// the CooWe media host. Rewrite any cooe-users-prod S3 URL -> media-beta.coowe.com/cooe-users-prod/
// <same path>, both URL forms (virtual-hosted `cooe-users-prod.s3[.region].amazonaws.com/<p>` and
// path-style `s3[.region].amazonaws.com/cooe-users-prod/<p>`). Applies to covers, avatars,
// attachments — every image the FE renders. No AWS URL is ever rendered.
// GENERAL: rewrite ANY S3 bucket -> media-beta.coowe.com/<bucket>/<same path>, both URL forms and
// any region. Covers cooe-users-prod (covers/avatars, recovers 200) AND cooe-messages-prod (chat
// attachments). Some cooe-messages-prod objects don't resolve on media-beta either (truly lost,
// data-recovery issue) — but per the rule we still never RENDER an amazonaws URL.
const MEDIA_BASE = "https://media-beta.coowe.com/"
const deAwsUrl = u => {
  if (!u || typeof u !== "string" || u.indexOf("amazonaws.com") === -1) return u
  return (
    u
      // Unsplash images served from S3 -> the Unsplash CDN host (NOT our media). These are a
      // third party (used for TeeUp covers); send the S3 form back to images.unsplash.com so the
      // cover still loads and no amazonaws URL is rendered. Must run BEFORE the cooe rules.
      .replace(
        /https?:\/\/s3[.-][a-z0-9.-]*amazonaws\.com\/images\.unsplash\.com\//gi,
        "https://images.unsplash.com/"
      )
      .replace(
        /https?:\/\/images\.unsplash\.com\.s3[.-][a-z0-9.-]*amazonaws\.com\//gi,
        "https://images.unsplash.com/"
      )
      // OUR media buckets only (cooe-*): the S3 objects moved to media-beta. Scope to cooe- so we
      // never rewrite a third-party S3 URL to media-beta (which would 404).
      .replace(
        /https?:\/\/(cooe-[a-z0-9-]+)\.s3[.-][a-z0-9.-]*amazonaws\.com\//gi,
        MEDIA_BASE + "$1/"
      )
      .replace(
        /https?:\/\/s3[.-][a-z0-9.-]*amazonaws\.com\/(cooe-[a-z0-9-]+)\//gi,
        MEDIA_BASE + "$1/"
      )
  )
}
// Recursively rewrite every string in a response payload (belt-and-suspenders: catches any URL
// field — message attachments, nested user objects — without enumerating each one).
const deAwsDeep = obj => {
  if (typeof obj === "string") return deAwsUrl(obj)
  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) obj[i] = deAwsDeep(obj[i])
    return obj
  }
  if (obj && typeof obj === "object") {
    for (const k in obj)
      if (Object.prototype.hasOwnProperty.call(obj, k)) obj[k] = deAwsDeep(obj[k])
    return obj
  }
  return obj
}

// Chat @mentions are stored in the message body as the mentioned user's LEGACY id (e.g. "@4324").
// Participant usernames encode that legacy id as their suffix ("sean_4324"), so as participants are
// seen we build a legacy-id -> username map and rewrite "@<legacyId>" -> "@<username>" in message
// text, so the chat shows "@sean_4324" instead of "@4324".
const legacyUsername = {}
const rememberLegacyUsername = username => {
  const u = String(username || "")
  const leg = u.split("_").pop()
  if (leg && /^\d+$/.test(leg)) legacyUsername[leg] = u
}
const rewriteMentions = text => {
  if (!text || text.indexOf("@") === -1) return text
  return text.replace(/@(\d+)/g, (m, id) =>
    legacyUsername[id] ? "@" + legacyUsername[id] : m
  )
}

// ---- response reshapers: dev -> raw beta ----

// dev list item -> raw beta teeup (consumed by formatTeeup + TeeupItem/propsMapper)
const devCoToBetaTeeup = co => ({
  id: aliasId(co.id),
  name: co.name,
  status: teeupStatusCode(co.status),
  // wave4-fix15: legacy `gameplans` (title "When"/"Where" + suggestions incl. the `when` object)
  // feed the calendar reducer (SET_TEEUPS -> detectTeeupCalendarDay) so the month/week grid
  // shows the user's TeeUps even with no external calendar connected.
  gameplans: reshapeGameplan(co.gameplan || [], co.participants),
  userstatus: userStatusCode(co.my_status),
  private: false,
  archived: false,
  gameplanOptions: [],
  photo: co.image_url || co.photo || null,
  createdby: co.created_by || co.creator_id || null,
  createdat: co.last_activity || null,
  updatedat: co.last_activity || null,
  lastActedAt: co.last_activity || null,
  newMessages: co.unread_count || 0,
  seen: !co.unread_count,
  participantsCount: co.participant_count || 0,
  // Beta gates the Suggest-when/where/decide/invite quick-actions on teeup.settings.*
  // (useActions reads selectActiveTeeup().settings). Dev is single-user and the signed-in
  // user owns their teeups, so grant the full organiser permission set; without this the
  // quick-action pills render permanently disabled and clicks are no-ops.
  settings: {
    allowInviteOthers: true,
    allowSelectGameplan: true,
    allowChangeGameplanOptions: true,
    allowDecideGameplan: true,
    allowChangeState: true,
    allowChangeTitle: true,
    allowPromoteToAllSetOrPlanning: true,
    allowPromoteToEnded: true,
    allowInviteLink: true,
    allowAutoScheduled: true,
  },
  // dev-API passthrough: integer counts now returned live on every CO row.
  // Leave undefined when absent — no defaults, no computation (fail-soft belongs to the card).
  agenda_count: co.agenda_count,
  task_count: co.task_count,
})

// Parse the inbox tab filter out of the request query (the beta filters server-side; the dev list
// returns ALL of the user's COs, so we filter here to match the tab).
const parseListFilter = url => {
  const q = (url || "").split("?")[1] || ""
  let p
  try {
    p = new URLSearchParams(q)
  } catch {
    p = { get: () => null }
  }
  return {
    category: p.get("category"), // "1" inbox, "2" archive
    scheduleFilter: p.get("scheduleFilter"), // scheduled | unscheduled
    userStatusFilter: p.get("userStatusFilter"), // invited | all
    teeupStatusFilter: p.get("teeupStatusFilter"), // ended | all
    // Wave4-scale: the beta FE's requested page (see prepareQueryParams: page 0 on a fresh
    // load/tab-switch, incremented by InfiniteScroll's handleNextPage). Used to drive real
    // offset-based pagination against the dev backend for the non-client-sub-filtered tabs
    // (Active/Archive/Ended) so the list can scroll past the first page up to total_count.
    page: parseInt(p.get("page") || "0", 10) || 0,
    // WB3: the beta client sends the inbox search box's text as `search` (see
    // useFetchTeeUps.prepareQueryParams). This was never read out of the query string here, so
    // reshapeList's `filter.search` was always undefined and the on-screen list never narrowed —
    // the client fired the network call with `search=...` but the shim silently dropped it before
    // the reshape step. Root cause, not a backend gap (dev has no full-text search route).
    search: p.get("search"),
  }
}

// Which inbox tab a dev CO belongs to (mirrors the beta server-side bucketing).
// Wave1 FIX-1: category (1=INBOX, 2=ARCHIVE, 3=TRASH) is now filtered SERVER-SIDE (the dev
// backend accepts ?category=N on the list endpoint — see the request interceptor below, which
// appends it). This function only applies the remaining SUB-filters (schedule/status/invited)
// within whatever category set the server already returned; it must never re-exclude by category.
const coMatchesTab = (co, f) => {
  // BUG-006 / BUG-053: ended AND cancelled teeups STAY in the inbox (greyed/archive-style) and
  // remain reachable — cancelled is a per-user INBOX item with a Cancelled badge, not a removal.
  // (Previously cancelled was filtered out of every tab, leaving it unreachable on web.)
  if (!f) return true
  const status = co.status
  const my = co.my_status
  if (f.teeupStatusFilter === "ended") return status === "ended" // Ended tab
  if (f.userStatusFilter === "invited") return my === "invited" // Invitations tab
  if (f.scheduleFilter === "scheduled")
    // BUG-036: use co.status === "scheduled" as the primary signal (mirrors Android's sched flag).
    // Fallback: respect is_scheduled for any teeup the backend marks as scheduled by some other path.
    return (co.status === "scheduled" || !!co.is_scheduled) && status !== "ended"
  if (f.scheduleFilter === "unscheduled")
    // BUG-037: planning teeups must appear in the Unscheduled tab. Android derives the
    // "scheduled" / "unscheduled" split from co.status === "scheduled", so planning teeups are
    // always unscheduled by status. Guard: also accept !co.is_scheduled for non-planning COs
    // the backend explicitly marks as unscheduled.
    return (co.status === "planning" || !co.is_scheduled) && status !== "ended" && status !== "cancelled"
  // Active (default INBOX tab): everything stays reachable — ended AND cancelled remain visible
  // (greyed, with their stage badge). BUG-053: cancelled must not vanish from the web inbox.
  return true
}

// Wave4-scale: shim-controlled page size for real offset pagination of the inbox list. Both the
// request interceptor (offset = page * LIST_PAGE_SIZE) and reshapeList (nextPage derivation) MUST
// use the same value or the offsets/next-page math drift.
const LIST_PAGE_SIZE = 500

// A tab is "clean" (server-side paginate-able) when reshapeList applies NO client-side sub-filter
// on top of the backend's own category/status filtering -- i.e. Active, Archive, Ended. For those,
// backend total_count is exact and offset pagination is lossless. Invitations/Scheduled/
// Unscheduled/search narrow the returned page client-side (coMatchesTab / searchValue), so their
// count can't come from total_count and their list isn't safely offset-paginated -- keep the old
// single-page behavior for them (no regression).
const listHasClientSubFilter = filter =>
  !!(
    filter &&
    (filter.search ||
      filter.scheduleFilter === "scheduled" ||
      filter.scheduleFilter === "unscheduled" ||
      filter.userStatusFilter === "invited")
  )

const reshapeList = (devData, filter) => {
  const all = Array.isArray(devData) ? devData : devData?.items || []
  // BUG: inbox search must filter by teeup name (search text comes through the filter object).
  const searchValue =
    (filter && (filter.searchValue || filter.search || filter.lnValue)) || ""
  const arr = all.filter(
    co =>
      coMatchesTab(co, filter) &&
      (!searchValue ||
        (co.name || co.title || "")
          .toLowerCase()
          .includes(searchValue.toLowerCase().trim()))
  )
  const teeups = arr.map(devCoToBetaTeeup)
  // The inbox hook stores data.participants into teeupParticipants, and the Card reads
  // teeupParticipants[teeup.id] then calls .find/.some/Object.values/slice on it — so EACH
  // teeup id MUST map to an ARRAY (empty is fine) or the card crashes the error boundary.
  // The dev list endpoint only gives participant_count; real participants come from detail.
  // The dev list endpoint already returns per-CO participants (with avatar_url) — map them into
  // teeupParticipants so the inbox cards show avatars (not just inside the TeeUp).
  const participants = {}
  const gameplanOptions = {}
  arr.forEach(co => {
    const tid = aliasId(co.id)
    participants[tid] = (co.participants || []).map(devParticipantToBetaUser)
    // INBOX-ARCH-003 fix: the TeeUpsNd Card reads useInboxStore.gameplanOptions[teeup.id] (fed
    // from this response's `gameplanOptions` map) to render the card's When/Where line via
    // getCardWhenInfo/getCardWhereInfo (src/utils/inbox.ts), which look for a DECIDED suggestion.
    // The dev list endpoint already returns each CO's full `gameplan` array (When/Where dimensions
    // + suggestions, including is_decided) in the SAME response — no extra network call needed.
    // Reuse the same reshapeGameplan() the detail page uses so the shape (and decided-derivation
    // in devSuggestionToBeta) is identical on list and detail. Previously this was left empty
    // (perf concern about the calendar-conflict worker); that worker only iterates DECIDED "when"
    // suggestions (see worker_checkCalendarConflicts.ts), so real per-CO data here is cheap.
    gameplanOptions[tid] = reshapeGameplan(co.gameplan, co.participants)
  })
  // Wave4-notif item 5 fix (root cause of "all wrong inbox numbers"): the dev list endpoint
  // already returns an authoritative, UNPAGINATED `total_count` for the server-side filters
  // (category/type/status) -- see api/routers/coordination_objects.py list_my_coordination_objects.
  // This used to be thrown away in favor of `teeups.length` (the length of whatever page of rows
  // happened to come back from THIS call, itself capped by the dev backend's default limit=200
  // since the FE's own limit/offset query params are never forwarded past the shim's request
  // interceptor -- see installDevBackendShim below). Confirmed live on QJ's real account: Archive
  // tab (category=2) showed "200 archived" (== the un-forwarded default limit) when the true
  // count is 2522 (`total_count` reports this correctly; the old code could never show more than
  // the page cap). For a tab with no CLIENT-side sub-filter (Active/Archive -- the two tabs QJ
  // actually looks at), `total_count` is exact. For tabs that DO apply a client-side sub-filter on
  // top of the server response (Invitations/Scheduled/Unscheduled/search -- coMatchesTab / the
  // searchValue branch above), `total_count` does not account for that narrowing, so fall back to
  // the filtered page's own length in that case (same behavior as before, not a regression) --
  // still bounded, still better than nothing; a fully correct count for those tabs needs
  // server-side sub-filtering (out of this ticket's scope).
  const hasClientSubFilter = listHasClientSubFilter(filter) || !!searchValue
  const total =
    !hasClientSubFilter && typeof devData?.total_count === "number"
      ? devData.total_count
      : teeups.length
  // Wave4-scale: real offset pagination for the clean tabs so the archive list scrolls past the
  // first page. The FE's InfiniteScroll (TeeUpList/index.tsx) advances by setting
  // pagination.page = pagination.nextPage, and the fetch hook APPENDS non-cleared pages. There are
  // more pages iff the NEXT page's starting offset is still inside total. For client-sub-filtered
  // tabs we keep a single page (nextPage null) exactly as before -- no regression.
  const requestedPage = (filter && filter.page) || 0
  const clean = !hasClientSubFilter
  const nextPage =
    clean && (requestedPage + 1) * LIST_PAGE_SIZE < total
      ? requestedPage + 1
      : null
  return {
    teeups,
    participants,
    gameplanOptions,
    states: {},
    messages: [],
    page: requestedPage,
    nextPage,
    previousPage: requestedPage > 0 ? requestedPage - 1 : null,
    total,
  }
}

// dev detail participant -> raw beta user (consumed by formatTeeupUsers/formatTeeupUser)
const devParticipantToBetaUser = p => (
  rememberLegacyUsername(p.username),
  {
  id: aliasId(p.user_id),
  user_id: aliasId(p.user_id),
  // WU-P2-FE-a (2026-07-09): the participant ROW's own id (participants.id) -- a DIFFERENT id
  // space than user_id. Agenda-item people refs (owner_participant_id/involved_people) are
  // participants.id, while task people refs (assignee_id/involved_people) are participants.
  // user_id (pre-existing backend schema asymmetry). Preserved here (aliased, same alias table)
  // so AgendaTasksSections' people-picker can resolve/seed agenda owners against the right id
  // space without a second raw-participants round trip.
  participantId: p.id != null ? aliasId(p.id) : null,
  // username is shown in the UI (e.g. "X invited you") AND drives profile-route nav
  // (/profile/{username}); use the real username (the dev backend resolves it for the profile
  // fetch). Falls back to display_name. Never the opaque alias id.
  username: p.username || p.display_name || String(aliasId(p.user_id)),
  name: p.display_name,
  avatar: p.avatar_url || "",
  backgroundImageUrl: p.background_image_url || "",
  status: userStatusCode(p.status) || 1,
  isOrganiser: p.role === "organizer",
  joinedAt: p.joined_at || null,
  invitedBy: aliasId(p.invited_by) || null,
  // Real per-user IANA timezone, now passed through by the backend (CO detail
  // participants[].timezone). No fallback to "UTC" -- an unset value must stay
  // null/undefined so the Time Zone group buckets that participant as Unknown
  // per the Multi-TZ Sync Up spec, never a fabricated default.
  timezone: p.timezone || null,
  contactList: [],
})

// beta conversationTypeIds (src/config/mappings.js)
const CT = {
  message: 1,
  suggestion: 2,
  status: 3,
  reply: 5,
  gameplanUpdate: 6,
  nudge: 10,
  image: 102,
  file: 103,
}

const toMs = iso => {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? undefined : t
}

// dev message -> raw beta backend message (consumed by receiveMessages/formatMessage)
const devMessageToBeta = m => {
  const body = m.body || {}
  const senderId = aliasId(m.user_id)
  const createdAt = toMs(m.created_at)
  const base = {
    id: m.id,
    senderId,
    createdAt,
    reactions: m.reactions && m.reactions.length ? m.reactions : undefined,
    isPinned: !!m.is_pinned,
    pinnedById: m.pinned_by_id ? aliasId(m.pinned_by_id) : null,
  }
  const text =
    typeof body.text === "string" ? rewriteMentions(body.text) : ""
  switch (m.type) {
    case "image":
      return {
        ...base,
        conversationTypeId: CT.image,
        image: body.url || body.imageUrl || body.image || text,
      }
    case "file":
      return { ...base, conversationTypeId: CT.file, file: body.file || body }
    case "nudge":
      return { ...base, conversationTypeId: CT.nudge, text: text || "Nudge" }
    case "shared_link":
      return {
        ...base,
        conversationTypeId: CT.message,
        text: text || "shared a link",
        mentionIds: (body.mentionIds || m.mentions || []).map(aliasId),
      }
    case "text":
    default:
      if (m.parent_id) {
        // RT1 (BUG-LC-CHROME-010 / EDGE-BUG-6): a message's own `id` above is the RAW dev-backend
        // UUID (`base.id = m.id`, never aliasId()'d). The reply-to-parent lookup on render
        // (chatUtils.js getMessageOptions: `messages.find(message => message.id === messageId)`)
        // therefore needs `messageId` in the SAME raw-UUID space, not the FE's int-alias space.
        // aliasId(m.parent_id) here produced an integer that could never equal any message's raw
        // UUID `id` -- so on a fresh GET /messages (page load/reload) the parent lookup always
        // failed and the reply rendered as a bare bubble with no quoted-parent preview (the
        // optimistic live-send path masked this: the shim's "reply" socket-emit handler
        // overrides messageId with the FE's own un-aliased payload.messageId right after send,
        // which happens to match, but that override does not apply to a subsequent GET fetch).
        return {
          ...base,
          conversationTypeId: CT.reply,
          text,
          messageId: m.parent_id,
          mentionIds: (body.mentionIds || m.mentions || []).map(aliasId),
        }
      }
      return { ...base, conversationTypeId: CT.message, text, mentionIds: (body.mentionIds || m.mentions || []).map(aliasId) }
  }
}

const reshapeMessages = devData => {
  const arr = Array.isArray(devData) ? devData : devData?.messages || []
  return {
    // BUG-058: drop blank message bubbles. A text/reply message whose body has no (non-whitespace)
    // text rendered an empty chat bubble. Keep every non-text type (image/file/nudge/shared_link),
    // which carry their own content even when the text is empty.
    messages: arr.map(devMessageToBeta).filter(m => {
      if (m.conversationTypeId === CT.message || m.conversationTypeId === CT.reply) {
        return typeof m.text === "string" && m.text.trim().length > 0
      }
      return true
    }),
  }
}

// FIX(11-R2): the dev backend stores reactions as one record per user
// (`[{user_id, reaction: "upvote"|"downvote"}]`) but every beta component that renders reactions
// (molecules/Reactions "who reacted" roster, ReactionsTab likes/dislikes, useReactions'
// upVote/downVote/userVote lookups, formatTeeupGameplanOptions -> teeupReactions seeding on load)
// reads the beta grouped shape `[{reactionId, reactedBy: [userId, ...]}]`. devSuggestionToBeta used
// to pass the dev array through RAW, so a reaction stored on the backend (yours from a prior
// session, or anyone else's) never matched a reactionId/reactedBy lookup and silently rendered as
// "no reaction" after a fresh load -- only reactions added live in the current tab (via the
// suggestion:react socket echo, which already emits the grouped shape) showed up. Group the dev
// per-user records into the beta shape here, aliasing each user_id the same way every other
// suggestion field does (createdBy, creator.id) so components can match reactedBy entries against
// aliased participant ids. Omit empty groups -- ReactionsTab's "no reactions" computation treats a
// present-but-empty reactedBy the same as absent, but keeping the array free of zero-length groups
// matches the shape the live socket-echo path already produces.
const mapDevReactionsToBeta = reactions => {
  if (!Array.isArray(reactions) || reactions.length === 0) return []
  const upvoteIds = []
  const downvoteIds = []
  reactions.forEach(r => {
    if (!r || !r.user_id) return
    if (r.reaction === "upvote") upvoteIds.push(aliasId(r.user_id))
    else if (r.reaction === "downvote") downvoteIds.push(aliasId(r.user_id))
  })
  const out = []
  if (upvoteIds.length) out.push({ reactionId: 2, reactedBy: upvoteIds })
  if (downvoteIds.length) out.push({ reactionId: 3, reactedBy: downvoteIds })
  return out
}

// dev gameplan suggestion -> raw beta suggestion (formatSuggestion input)
const GP_TITLES = { when: "When", where: "Where", starts_when: "Starts When" }
// FIX-4a: the dev backend's suggestion carries created_by_id (a raw participant user_id) but no
// embedded creator object, so formatSuggestion's `creator` (which SuggestionItem.js reads via
// suggestion.creator?.name/.avatar) always came through undefined -- the expanded Game Plan list
// showed a value with no attribution even though the data existed. Resolve it here against the
// CO's participants list (passed down from reshapeGameplan) into the {id, name, avatar} shape
// SuggestionItem already expects.
const devSuggestionToBeta = (s, dimension, optionId, participants = []) => {
  const v = s.value || {}
  const creatorParticipant = participants.find(
    p => p.user_id === s.created_by_id
  )
  const creator = creatorParticipant
    ? {
        id: aliasId(creatorParticipant.user_id),
        name: creatorParticipant.display_name,
        avatar: creatorParticipant.avatar_url || "",
      }
    : undefined
  // DECIDED-ROW PARITY (mirrors the iOS mapSuggestion fix): the dev backend keeps a single
  // is_decided flag and leaves is_selected=false even on a decided suggestion. The web Game Plan
  // row (TeeupGameplan.js) and the suggestion-popup decide/undecide control (usePopupActions.js)
  // BOTH read the suggestion's `selected`/`isSelected` (NOT `decided`/`isDecided`) to (a) render
  // the locked decided value on the When/Where row and (b) flip the action label to
  // "Set <dim> as Undecided". Mapping selected only from is_selected meant a decided suggestion
  // showed a blank row and never offered Undecide. Derive selected from (is_selected || is_decided)
  // so a dev-decided suggestion drives the decided row state and the undecide toggle.
  const decided = !!s.is_decided
  const selected = !!s.is_selected || decided
  // PINBOARD (2026-07): the backend gameplan payload carries is_pinned (+ pinned_by_id) on each
  // suggestion once POST .../suggestions/{sid}/pin lands (deployed in parallel with this fix).
  // Carry both onto the beta suggestion shape so a page reload rehydrates pin state — without this
  // completeCurrentSuggestion?.isPinned (usePopupActions' popup toggle) always read undefined after
  // a fresh load even for a suggestion pinned in a prior session.
  const isPinned = !!s.is_pinned
  const pinnedById = s.pinned_by_id != null ? aliasId(s.pinned_by_id) : undefined
  const base = {
    id: aliasId(s.id),
    optionId,
    // suggestion-level type drives the display formatter (useSuggestionData):
    // "when" -> date/time title, else value as-is.
    type: dimension === "where" ? "where" : "when",
    isSelected: selected,
    selected: selected,
    isDecided: decided,
    decided: decided,
    createdBy: aliasId(s.created_by_id),
    creator,
    reactions: mapDevReactionsToBeta(s.reactions),
    isPinned,
    pinnedById,
  }
  if (dimension === "where") {
    return {
      ...base,
      value: v.name || v.value || "",
      details: v.details || v.url || "",
      // BUG-RCNEXT-WEB-001 root: the beta decodeURIComponents fullDetails, so emit it URI-encoded
      // (dev stores raw). Guarded by safeDecode in gamePlanUtils, but encoding here keeps the
      // contract correct so the decoded value round-trips instead of rendering garbled %2F.
      fullDetails: encodeURIComponent(v.details || v.url || ""),
      url: v.url || "",
      urlType: v.url_type,
      locationType: v.location_type,
      latitude: v.latitude,
      longitude: v.longitude,
      googlePlaceId: v.google_place_id,
    }
  }
  // when / starts_when
  const start = v.start || v.begin || v.end
  const end = v.end || v.start
  return {
    ...base,
    value: v.value || "",
    startDate: toMs(start),
    endDate: toMs(end),
    duration: v.duration,
    isTimeZoneEnabled: !!v.timezone,
    // wave4-fix15: the calendar reducer's detectTeeupCalendarDay() reads the LEGACY
    // suggestion.when = {type, value, endDate} shape to place a TeeUp on the month/week grid.
    // Emit it alongside startDate/endDate so TeeUps render as calendar events.
    when: start
      ? { type: "Time", value: start, endDate: end || start }
      : undefined,
  }
}
const reshapeGameplan = (devGameplan, participants = []) =>
  (devGameplan || []).map(opt => {
    const optionId = aliasId(opt.id)
    return {
      id: optionId,
      optionId,
      type: opt.dimension === "starts_when" ? "startsWhen" : opt.dimension,
      title: GP_TITLES[opt.dimension] || opt.dimension,
      icon: opt.dimension,
      isPrimary: false,
      statusId: undefined,
      sourceType: undefined,
      suggestions: (opt.suggestions || []).map(s =>
        devSuggestionToBeta(s, opt.dimension, optionId, participants)
      ),
    }
  })

// dev /coordination-objects/{id} -> the overview shape fetchTeeupOverview destructures
const reshapeDetail = dev => {
  const users = (dev.participants || []).map(devParticipantToBetaUser)
  const teeup = {
    ...devCoToBetaTeeup(dev),
    name: dev.name,
    description: dev.description || "",
    gameplanOptions: [],
  }
  const gameplanOptions = reshapeGameplan(dev.gameplan, dev.participants)
  // BUG-003a / BUG-036: the dev backend does NOT promote co.status when suggestions are decided, so
  // a locked TeeUp kept status "planning" and the detail header rendered "Planning" for an All-Set
  // (or Scheduled) TeeUp. Derive the display stage from the decided Game Plan:
  //   When AND Where decided -> All Set (2);  only When decided -> Scheduled (7);  else Planning (1).
  // Never downgrade a terminal/in-progress stage the backend already set (ended/cancelled/happening).
  const baseStatus = teeup.status
  if (baseStatus === 1 || baseStatus === 2 || baseStatus === 7) {
    const whenOpt = gameplanOptions.find(o => o.type === "when")
    const whereOpt = gameplanOptions.find(o => o.type === "where")
    const whenDecided = !!whenOpt?.suggestions?.some(s => s.isDecided)
    const whereDecided = !!whereOpt?.suggestions?.some(s => s.isDecided)
    if (whenDecided && whereDecided) teeup.status = 2 // ALL_SET
    else if (whenDecided) teeup.status = 7 // SCHEDULED
    else teeup.status = 1 // PLANNING
  }
  return {
    teeup,
    users,
    organisers: users.filter(u => u.isOrganiser),
    invited: [],
    history: [],
    gameplanOptions,
    locations: [],
    // No `messages` key -> getTeeupById falls through to fetchMessages (GET /teeups/{id}/messages),
    // which the shim routes to the dev messages endpoint + reshapes.
  }
}

// Suggestion-as-message bridge. Beta stores every suggestion ALSO as a suggestion-message in
// activeTeeup.messages; the Suggestion popup resolves `completeCurrentSuggestion` via
// selectMessageById(suggestion.id) off that list (Withdraw/Restore/Pinboard read its id +
// isWithdrawn/isPinned). The dev backend keeps suggestions out of the messages endpoint, so on a
// fresh load the popup found nothing and Withdraw fired DELETE /suggestions/undefined. Fetch the CO
// gameplan and emit one synthetic message per suggestion. typeId = statusTypeIds.retract (5) keeps
// them OUT of the chat render (selectMessagesWithoutNugdes filters typeId===5) while staying in the
// unfiltered selectMessages that selectMessageById reads — zero chat-render impact.
const fetchSuggestionMessagesForCo = async coId => {
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/coordination-objects/${coId}?token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return []
    const detail = await res.json()
    const out = []
    for (const opt of reshapeGameplan(detail.gameplan, detail.participants)) {
      for (const s of opt.suggestions || []) {
        out.push({
          ...s, // id (aliased suggestion id), startDate/value/details, isSelected, isDecided, reactions
          // conversationTypeId MUST be `suggestion` (2): formatMessage drops any message whose
          // conversationTypeId isn't a known type, and the suggestion case sets message.id =
          // event.suggestionId. Without this the injected rows were silently discarded and Withdraw
          // still saw `undefined`. With it they land in selectMessages so selectMessageById(id)
          // resolves completeCurrentSuggestion -> Withdraw/Pinboard get the real id.
          conversationTypeId: 2,
          suggestionId: s.id,
          gameplanOptionId: s.optionId,
          isWithdrawn: false,
          // s.isPinned/pinnedById already come from devSuggestionToBeta (is_pinned/pinned_by_id on
          // the raw dev suggestion) -- carry them through so a reload rehydrates the pinned state
          // instead of always resetting to unpinned.
          isPinned: !!s.isPinned,
          pinnedById: s.pinnedById,
        })
      }
    }
    // FIX-6c: decide narrative row. After the Organizer decides a When/Where the Conversation is
    // spec'd to gain a narrative row ("X marked When: <value>", never a "Decided" badge/chip) --
    // today it only gains the plain suggestion-message row above. The dev backend has no decide
    // EVENT at all (GET /messages never returns one) and the suggestion object itself carries no
    // decided_by/decided_at -- only is_decided (a flag) plus created_by_id/created_at (who/when it
    // was SUGGESTED, not decided). That's a real data gap for exact decider identity + decide
    // timestamp (backend ticket candidate: emit decided_by/decided_at, or a decide event on the
    // messages endpoint). Best-effort per product rule (only the Organizer can decide a Game Plan
    // item -- see canDecideGameplan/allowDecideGameplan): attribute the narrative to the CO's
    // organizer participant, ordered just after the suggestion's own createdAt so it reads as a
    // follow-up in the Conversation. This only synthesizes the narrative row; it never fakes who
    // created the suggestion (that stays the real created_by_id via FIX-4a).
    const organizer = (detail.participants || []).find(
      p => p.role === "organizer"
    )
    for (const opt of detail.gameplan || []) {
      for (const s of opt.suggestions || []) {
        if (!s.is_decided) continue
        out.push({
          id: `decide-${aliasId(s.id)}`,
          conversationTypeId: CT.gameplanUpdate,
          userId: organizer
            ? aliasId(organizer.user_id)
            : aliasId(s.created_by_id),
          createdAt: (toMs(s.created_at) || Date.now()) + 1000,
          isDecided: true,
          isGamePlan: true,
          suggestionId: aliasId(s.id),
        })
      }
    }
    return out
  } catch (e) {
    return [] // non-fatal: Withdraw just won't resolve, everything else unaffected
  }
}

// dev user -> beta profile (profile page / avatar-click navigation). The profile page reads a
// NESTED `userInfo.profile.{id,name,username,avatar,about,backgroundImageUrl}` and treats
// contactMechanisms/tags as ARRAYS (.map/.length).
// PERSON-PROFILE (2026-07-04): GET /users/{id}/view (dev devPath /v1/users/{id}, kind "profile")
// now returns about/location/contact_mechanisms for the VIEWED user (backend PERSON-PROFILE fix,
// api/routers/auth.py get_user) -- previously hardcoded empty here, so the profile page's Contact
// Info tab always rendered "No information" no matter what the target user had actually set.
// `contact_mechanisms` off the backend is already PUBLIC-ONLY (server-side filter), so every row
// here is safe to render without a further isPublic check on this side.
const reshapeProfile = u => {
  const id = aliasId(u.id)
  const name = u.display_name
  const username = u.username || String(id)
  const avatar = u.avatar_url || ""
  const bg = u.background_image_url || ""
  const about = u.about || ""
  const location = u.location || ""
  const contactMechanisms = Array.isArray(u.contact_mechanisms) ? u.contact_mechanisms : []
  return {
    id,
    userId: id,
    name,
    firstName: name,
    lastName: "",
    username,
    nickname: name,
    email: u.email || "",
    avatar,
    avatarUrl: avatar,
    backgroundImageUrl: bg,
    timezone: u.timezone || "UTC",
    about,
    bio: about,
    isVip: false,
    profile: {
      id,
      name,
      username,
      avatar,
      about,
      hometown: location,
      backgroundImageUrl: bg,
    },
    interests: [],
    tags: [],
    userTags: [],
    matchings: [],
    attributes: [],
    connections: [],
    mutualTeeups: [],
    primaryLocation: location || null,
    locations: [],
    settings: {
      calendarSync: { customTimezone: u.timezone || null, selectedCalendars: [] },
      notifications: {},
      contacts: {},
    },
    contactMechanisms,
  }
}

// dev contact -> beta contact; beta reads response.data.data (a list)
const devContactToBeta = c => ({
  id: c.id,
  name: c.display_name,
  firstName: c.first_name || c.display_name,
  lastName: c.last_name || "",
  fullName: c.display_name,
  company: c.company || "",
  jobTitle: c.job_title || "",
  // InviteSearch reads emails[0].email and phoneNumbers[0].number — keep these as object arrays.
  emails: (c.emails || []).map(e => (typeof e === "string" ? { email: e } : e)),
  phones: c.phones || [],
  phoneNumbers: (c.phones || []).map(p =>
    typeof p === "string" ? { number: p } : p
  ),
  avatar: c.avatar_url || "",
  avatarUrl: c.avatar_url || "",
  isUser: !!c.is_user,
  isVip: false,
  tags: [],
  contactList: [],
})
const reshapeContacts = devData => {
  const arr = Array.isArray(devData) ? devData : devData?.contacts || []
  return { data: arr.map(devContactToBeta) }
}

const devDirUser = u => ({
  id: aliasId(u.id),
  userId: aliasId(u.id),
  name: u.display_name,
  username: u.username || u.display_name,
  avatar: u.avatar_url || "",
  backgroundImageUrl: u.background_image_url || "",
  usertype: 1, // regular cooe user (formatCooeUser treats non-draft as "cooeUser")
  isVip: false,
  tags: [],
  contactList: [],
  emails: [],
  phoneNumbers: [],
  // PEOPLE-GRID (BUG1): pass the backend's already-correct shared-TeeUp count through --
  // it was silently dropped here before, which left any consumer that re-sorts by `.count`
  // client-side (teeupUtils.frequentSorting) sorting on `undefined` instead of the real
  // signal. The dev /v1/directory query already orders by this DESC server-side, so this
  // was previously masked (the redundant client no-op re-sort happened to leave that order
  // alone), but any consumer that filters/slices before re-sorting would not have been.
  count: u.shared_teeups || 0,
})
// dev directory user (CooWeverse search) -> flat beta user list
const reshapeDirectory = devData => {
  const arr = Array.isArray(devData) ? devData : devData?.users || []
  return arr.map(devDirUser)
}

// ---- /users/search (batchVerifyAndGetUserInfo) dev shim -----------------------------------
// BUG-RCNEXT-B1-INVITE-WEB: batchVerifyAndGetUserInfo() (src/actions/batchVerifyAndGetUserInfo.js)
// is a RAW fetch, not an axios call through the client this shim installs interceptors on, so it
// can never be caught by routeBetaToDev/the request interceptor below -- it always hit the real
// beta path (/users/search), which has no dev-backend route and 404s. Called DIRECTLY from that
// action file when isDevBackend() is true (see the early-return there). Resolves each searched
// value (the web only ever sends one identifier per selected contact -- an email, phone, or, for
// an already-known CooWe user chip, their username) against the REAL dev directory
// (/v1/directory?q=) so a selected CooWe user attaches by real id on Send instead of degrading to
// a fabricated manual email/phone invite (formatRest treating "maya_chen" as an email). Mirrors
// the beta backend's {users, contacts, rest} response shape:
//   users   -> resolved CooWe app users (spread directly into the invitee list by the caller)
//   contacts -> phone-book contacts matched to a CooWe user (dev has no such matching -- always [])
//   rest    -> values nothing matched (caller invites these as manual email/phone chips)
export const shimBatchVerifyAndGetUserInfo = async values => {
  const token = getToken()
  const users = []
  const rest = []
  for (const raw of values || []) {
    const value = String(raw || "").trim()
    if (!value) continue
    let hit = null
    try {
      const res = await fetch(
        `${API_BASE}/v1/directory?q=${encodeURIComponent(value)}&token=${encodeURIComponent(
          token
        )}`,
        { headers: { "Content-Type": "application/json" } }
      )
      if (res.ok) {
        const data = await res.json()
        const list = Array.isArray(data) ? data : data?.users || []
        const low = value.toLowerCase()
        // The FE sends one specific identifier per value (an already-resolved contact's
        // username, or a typed email/name) -- require an exact username/display_name match so an
        // unrelated same-prefix directory row (e.g. "Maya Rosa" while searching "maya_chen")
        // never gets silently invited instead.
        hit =
          list.find(u => (u.username || "").toLowerCase() === low) ||
          list.find(u => (u.display_name || "").toLowerCase() === low) ||
          null
      }
    } catch (e) {
      // non-fatal: value falls through to `rest` (manual invitee) below
    }
    if (hit) {
      // devDirUser() shape (aliased id, avatar, username, ...) + `type: "cooeUser"` -- matches
      // utils/contactUtils.js userTypes.COO_E / isCooeUser() (checked by
      // handleSendInvitations -> formatInvitees), needed since these entries are spread directly
      // into the invitee list, never passed through formatContact().
      users.push({ ...devDirUser(hit), type: "cooeUser" })
    } else {
      rest.push(value)
    }
  }
  return { users, contacts: [], rest }
}
// /users/suggest-invitees: the Contacts page reads {mostTeeupedWithUsers, previousTeeups}.
const reshapeInviteeSuggestions = devData => {
  const arr = Array.isArray(devData) ? devData : devData?.users || []
  return { mostTeeupedWithUsers: arr.map(devDirUser), previousTeeups: [] }
}

const reshapeMe = u => ({
  id: aliasId(u.id),
  userId: aliasId(u.id),
  name: u.display_name,
  // wave4-fix20: the dev backend NOW exposes the real users.username (p0func ITEM3 + PATCH write
  // path); prefer it so the @handle renders and the edit form shows what was actually saved.
  username: u.username || u.display_name,
  // wave4-fix7: the popover renders @{nickname}. The dev backend's /users/me has NO
  // username/handle field (confirmed live: id/org_id/display_name/email/timezone/avatar_url/
  // background_image_url/preferences only), so map any future username/nickname through and
  // leave it null otherwise -- the popover now hides the @ line when null (was rendering a
  // bare "@").
  nickname: u.username || u.nickname || null,
  email: u.email || "",
  avatar: u.avatar_url || "",
  backgroundImageUrl: u.background_image_url || "",
  // Wave4-P0FUNC ITEM3/profile-restore: /users/me now also returns about/phone/location/
  // linkedin/twitter (persisted in the users.preferences jsonb column, see api/routers/auth.py).
  // Map straight through; MyProfileNd.js prefers these over the legacy contactMechanisms path
  // (which is 404-shimmed to always-empty on dev, WL2).
  about: u.about || null,
  phone: u.phone || null,
  primaryLocation: u.location || null,
  linkedin: u.linkedin || null,
  twitter: u.twitter || null,
  url: u.url || null,
  publicEmail: u.publicEmail || null,
  timezone: u.timezone || "UTC",
  // App.js gates the whole app on settings.calendarSync OWNING a "customTimezone" key. Prefer the
  // backend's real settings (now served with calendarSync); fall back to a synthesized one so the
  // gate always passes.
  settings:
    u.settings && u.settings.calendarSync
      ? {
          notifications: {},
          contacts: {},
          ...u.settings,
          calendarSync: {
            customTimezone: u.timezone || null,
            selectedCalendars: [],
            additionalCalendars: [],
            syncTeeups: false,
            ...u.settings.calendarSync,
          },
        }
      : {
          calendarSync: {
            customTimezone: u.timezone || null,
            selectedCalendars: [],
            additionalCalendars: [],
            syncTeeups: false,
          },
          notifications: {},
          contacts: {},
        },
  contactMechanisms: { phone: [], email: [], location: [], url: [] },
  interests: [],
})

// ---- path routing: beta -> dev ----
// Returns { devPath, kind } or null (no mapping -> leave as-is / safe-empty).
const stripQuery = p => p.split("?")[0]
const norm = p => "/" + stripQuery(p).replace(/^\/+/, "").replace(/\/+$/, "")

const routeBetaToDev = (urlPath, method) => {
  const p = norm(urlPath)
  const m = (method || "get").toLowerCase()

  // list (paginated + plain)
  if (p === "/users/teeups/paginated" || p === "/users/teeups") {
    return { devPath: "/v1/users/me/coordination-objects", kind: "list" }
  }
  // create a TeeUp: POST /teeups -> a full create+seed sequence owned by the createTeeup adapter.
  // NOTE: no devPath here on purpose — a devPath would make the request interceptor take the plain
  // single-call path and never install the multi-step adapter (which creates the CO AND seeds the
  // create-time When/Where suggestions). The adapter handles the whole sequence itself.
  if (p === "/teeups" && m === "post") {
    return { kind: "createTeeup" }
  }
  // logged-in user
  if (p === "/users" && m === "get") {
    return { devPath: "/v1/users/me", kind: "me" }
  }
  // profile (avatar-click nav): users/{id}/view  OR  users/@{username}/view/{cur}
  const prof = p.match(/^\/users\/@?([^/]+)\/view(?:\/[^/]+)?$/)
  if (prof && m === "get") {
    return { devPath: `/v1/users/${unaliasId(prof[1])}`, kind: "profile" }
  }
  // Settings write: PUT users/settings -> dev PUT /users/me/settings
  if (p === "/users/settings" && (m === "put" || m === "patch")) {
    return { devPath: "/v1/users/me/settings", kind: "settingsWrite", method: "PUT" }
  }
  // WB2: profile name/avatar edit -> dev PATCH /v1/users/me (route added Wave3; previously only
  // GET existed -- PATCH/PUT both 405'd live). This was completely unmapped before, so the edit
  // form's PATCH users/profiles fell through to a raw beta path that 404s on dev -- the save
  // silently did nothing and the old name kept rendering (not a client-cache bug, a missing route).
  if (p === "/users/profiles" && m === "patch") {
    return { devPath: "/v1/users/me", kind: "profileWrite", method: "PATCH" }
  }
  // Notifications list -> dev (reshape); mark-read passthrough handled below
  if (p === "/users/notifications" && m === "get") {
    return { devPath: "/v1/users/notifications", kind: "notifications" }
  }
  // NC1: unread count badge -> dev /v1/users/notifications/unread-count ({unread: N})
  if (p === "/users/notifications/unread-count" && m === "get") {
    return { devPath: "/v1/users/notifications/unread-count", kind: "passthrough" }
  }
  // wave4-notif-conform: per-type unread counts for the category rail ({type: N}).
  if (p === "/users/notifications/unread-count-by-type" && m === "get") {
    return {
      devPath: "/v1/users/notifications/unread-count-by-type",
      kind: "passthrough",
    }
  }
  // Wave4-notif item 1: mark-all-read (bell-panel open) -> dev PUT /v1/users/notifications/read-all.
  // Must be checked BEFORE the single-notification /{id}/read regex below (that regex would never
  // match this literal path anyway since it has no id segment, but keeping the explicit literal
  // check first documents the precedence and avoids relying on regex non-match ordering).
  if (p === "/users/notifications/read-all" && m === "put") {
    return {
      devPath: "/v1/users/notifications/read-all",
      kind: "passthrough",
      method: "PUT",
    }
  }
  // NC1: mark-read -> dev PUT /v1/users/notifications/{id}/read
  const notifRead = p.match(/^\/users\/notifications\/([^/]+)\/read$/)
  if (notifRead && m === "put") {
    return {
      devPath: `/v1/users/notifications/${unaliasId(notifRead[1])}/read`,
      kind: "passthrough",
      method: "PUT",
    }
  }
  // Contacts (the beta Contacts section) -> dev /users/me/contacts
  if (p === "/users/contacts" && m === "get") {
    return { devPath: "/v1/users/me/contacts", kind: "contacts" }
  }
  // secondary contacts-section calls the dev backend doesn't serve — safe-empty (no rejections)
  // BUG (web people-picker zero-matches root cause): getContactsWithTags (createTeeupActions.js)
  // does `let tags = tagsData && tagsData.data ? tagsData.data : {}` then unconditionally
  // `tags.forEach(...)`. Safe-emptying this as a bare `{}` made `tagsData.data` undefined ->
  // tags fell to `{}` (an OBJECT) -> `.forEach` threw a TypeError, which aborted the whole
  // try-block BEFORE `dispatch(GOT_CONTACTS...)` ever ran -- so the CooWe-directory-derived
  // contact list (mostTeeupedWithUsers, fetched fine, 200) never made it into Redux and every
  // people-picker search came back empty regardless of what the API returned. Match the beta
  // contract shape (`{data: [...]}`, an ARRAY) so `.forEach` succeeds and GOT_CONTACTS dispatches.
  if (m === "get" && p === "/users/tags") return { kind: "emptyTags" }
  if (
    m === "get" &&
    (p === "/users/connections" ||
      p === "/users/matchings" ||
      p === "/users/interests" ||
      p === "/interests")
  ) {
    return { kind: "emptyArr" }
  }
  // Wave5-contacts (2026-07-03): Sync-Contacts connector bookkeeping is now REAL on dev
  // (api/routers/contacts.py POST/GET/DELETE .../contacts/connect(ors)) -- was previously
  // safe-emptied here as a bare [] array, which is what caused the A1 crash: contactActions.js
  // getContactsConnectors() reads `data.connectors` off the response, and a bare [] has no
  // .connectors field, so `.length` threw. The real route returns the proper {connectors:[...]}
  // envelope, so route to it instead of stubbing.
  if (p === "/users/contacts/connectors" && m === "get") {
    return { devPath: "/v1/users/contacts/connectors", kind: "passthrough" }
  }
  if (p === "/users/contacts/connect" && m === "post") {
    return { devPath: "/v1/users/contacts/connect", kind: "passthrough" }
  }
  // ACCOUNT-STATE bug B(ii) (2026-07-04): count-only summary of the signer's OWN
  // public.contacts, split by source (device/mobile-synced vs web Google-People import) -- see
  // api/routers/contacts.py my_contacts_summary(). Real route, real passthrough.
  if (p === "/users/me/contacts/summary" && m === "get") {
    return { devPath: "/v1/users/me/contacts/summary", kind: "passthrough" }
  }
  const contactConnector = p.match(/^\/users\/contacts\/connector\/([^/]+)$/)
  if (contactConnector && m === "delete") {
    return {
      devPath: `/v1/users/contacts/connector/${contactConnector[1]}`,
      kind: "passthrough",
      method: "DELETE",
    }
  }
  // Contacts page invitee suggestions (frequent/CooWeverse) -> {mostTeeupedWithUsers, previousTeeups}
  if (p === "/users/suggest-invitees" && m === "get") {
    return { devPath: "/v1/directory", kind: "inviteeSuggestions" }
  }
  // CooWeverse search -> flat directory list
  if (p === "/communities/search" && m === "get") {
    return { devPath: "/v1/directory", kind: "directory" }
  }
  // teeup detail overview: /teeups/{id} (id is an integer alias -> reverse to dev uuid)
  const detail = p.match(/^\/teeups\/([^/]+)$/)
  if (detail && m === "get") {
    return {
      devPath: `/v1/coordination-objects/${unaliasId(detail[1])}`,
      kind: "detail",
    }
  }
  // per-teeup users list: GET /teeups/{id}/users -> dev CO detail, reshaped to {users:[...]}.
  // ROOT CAUSE of the post-create 404 (CREATE-AI-POLISH, 2026-07-03): createTeeupActions.js's
  // createTeeup() (shared by BOTH the classic form and the new create-ai flow) always fires
  // fetchTeeupUsersById(teeupId) right after create via Promise.all([...]) — see teeupActions.js
  // fetchTeeupUsersById(), which GETs endpoints.teeup.users(id) = "teeups/{id}/users". That path
  // had NO mapping here, so it fell through to the raw beta path against API_BASE (which only
  // serves /api/v1/*) and 404'd on every single create. fetchTeeupUsersById's own .catch swallows
  // the rejection (creation itself never broke) but still logs a console.error — the "caught 404 +
  // console error" symptom, reproduced identically on classic since both flows share this action.
  const teeupUsers = p.match(/^\/teeups\/([^/]+)\/users$/)
  if (teeupUsers && m === "get") {
    return {
      devPath: `/v1/coordination-objects/${unaliasId(teeupUsers[1])}`,
      kind: "teeupUsers",
    }
  }
  // aux list endpoints the inbox bootstrap fires — safe-empty (enriched later)
  if (p === "/users/teeups/users") return { kind: "emptyUsers" }
  if (p === "/users/teeups/gameplan-options") return { kind: "emptyGO" }
  if (p === "/users/teeups/state") return { kind: "emptyObj" }
  // ROOT CAUSE FIX (clear-on-open, 2026-07-09): opening a TeeUp calls readTeeup(id) ->
  // PUT /users/teeups/{id}/state/seen, the backend endpoint that bulk-marks ALL of that co's
  // notifications read for the signer (api/routers/notifications.py mark_teeup_seen). This shim
  // previously short-circuited BOTH the per-teeup and bulk variants to a no-op {} (stale stubs from
  // when the backend had no such routes -- see the superseded catch-all regex below), so the
  // durable clear-on-open never ran and the inbox unread dot never cleared. Both routes are now
  // real on the dev backend; pass them through, mirroring the existing /read passthrough (NC1
  // above). Per-teeup MUST be checked before the bulk literal-path match since a bulk match would
  // never fire on an id-bearing path anyway, but ordering documents precedence.
  const teeupStateSeen = p.match(/^\/users\/teeups\/([^/]+)\/state\/seen$/)
  if (teeupStateSeen && m === "put") {
    return {
      devPath: `/v1/users/teeups/${unaliasId(teeupStateSeen[1])}/state/seen`,
      kind: "passthrough",
      method: "PUT",
    }
  }
  // bulk "mark all as read" variant (PUT /users/teeups/state/seen, no id segment) -- also real on
  // dev now (mark_teeups_seen_bulk); pass through the same way.
  if (p === "/users/teeups/state/seen" && m === "put") {
    return { devPath: "/v1/users/teeups/state/seen", kind: "passthrough", method: "PUT" }
  }
  // per-teeup "mark seen" GET or other non-PUT hits on {id}/state(/seen) — dev has no such route
  // for non-PUT; the bare path match above misses the id segment, so it fell through to a real
  // 404. Safe-empty it. (PUT .../state/seen is handled by the real passthroughs above.)
  if (/^\/users\/teeups\/[^/]+\/state(\/seen)?$/.test(p)) return { kind: "emptyObj" }
  // post-Auth0 identity pre-check: the social login flow POSTs this and only proceeds (sets
  // isIdentityVerified) on a 2xx. The dev backend has no such route, so the call would throw and
  // bounce back to /sign-in (the "loops back" symptom). Answer it OK on dev.
  if (p === "/users/check-identity") return { kind: "emptyObj" }
  if (p === "/users/check-identity/" || p === "users/check-identity") return { kind: "emptyObj" }
  // invite users: POST /teeups/{id}/invitees {invited:[...]} -> N x dev POST participants
  const inv = p.match(/^\/teeups\/([^/]+)\/invitees$/)
  if (inv && m === "post") {
    return { kind: "invite", teeupId: inv[1] }
  }
  // email/SMS invite send -> safe success (external comms out of scope on dev)
  if (/^\/teeups\/[^/]+\/invitees\/send$/.test(p) && m === "post") {
    return { kind: "emptyObj" }
  }
  // per-teeup gameplan refetch: GET /teeups/{id}/gameplan-options -> dev CO detail, return its
  // reshaped gameplan array. The post-suggestion live-refresh (fetchGameplanOptions) hit this and
  // 404'd, then crashed downstream reading gameplans[..].suggestions. (The bare-path safe-empty
  // above never matched the id-bearing path.)
  const go = p.match(/^\/teeups\/([^/]+)\/gameplan-options$/)
  if (go && m === "get") {
    return {
      devPath: `/v1/coordination-objects/${unaliasId(go[1])}`,
      kind: "gameplanOptions",
    }
  }
  // RSVP change (BUG-049): PUT /teeups/{id}/status/user {userId, statusId} -> dev
  // PUT /coordination-objects/{uuid}/participants/me/status. Without this the web RSVP-change
  // (updateUserStatus) hit the dev backend at /teeups/{id}/status/user (no such route) -> 404, so
  // changing attendance on web silently failed. joinToTeeup uses /status/user/sharedlink and is NOT
  // matched here (the $ anchor excludes the trailing segment).
  const ust = p.match(/^\/teeups\/([^/]+)\/status\/user$/)
  if (ust && (m === "put" || m === "post")) {
    return { kind: "userStatusChange", teeupId: ust[1] }
  }
  // BUG-RCNEXT-WEB-002: inbox category change (Dismiss/Decline) PUT /teeups/{id}/categories/{n}.
  // Dev has no category endpoint (it 404'd); map it to the reversible RSVP route below.
  const cat = p.match(/^\/teeups\/([^/]+)\/categories\/([0-9]+)$/)
  if (cat && (m === "put" || m === "post")) {
    return { kind: "categoryChange", teeupId: cat[1], categoryId: Number(cat[2]) }
  }
  // teeup chat: /teeups/{id}/messages -> dev /coordination-objects/{id}/messages (reshape)
  const msgs = p.match(/^\/teeups\/([^/]+)\/messages$/)
  if (msgs && m === "get") {
    return {
      devPath: `/v1/coordination-objects/${unaliasId(msgs[1])}/messages`,
      kind: "messages",
      coId: unaliasId(msgs[1]),
    }
  }
  // teeup-detail aux calls (organizers / invite-history) — safe-empty for now
  if (/^\/teeups\/[^/]+\/(organizers|organisers|invite-history)$/.test(p) && m === "get") {
    return { kind: "emptyArr" }
  }
  // token refresh — keep the dev token
  if (p === "/users/token" || p === "users/token") return { kind: "tokenRefresh" }

  // Wave 2 calendar-web contract-drift fix: connect/list/disconnect/write-target calls were
  // UNMAPPED here — they fell through to passthrough and hit /api/users/calendars (a beta path
  // that doesn't exist on the dev backend, which only serves /api/v1/users/calendars), a silent
  // 404 on every connect-save, list, unlink, and (nonexistent-on-web) write-target set. Map them
  // to the REAL routes: GET/POST /v1/users/calendars, DELETE /v1/users/calendars/{id}, PATCH
  // /v1/users/calendars/{cal}/write-target. Response shapes already match what the FE expects
  // ({calendars: [...]})  no reshape needed.
  const calWriteTarget = p.match(/^\/users\/calendars\/([^/]+)\/write-target$/)
  if (calWriteTarget && (m === "patch" || m === "put")) {
    return {
      devPath: `/v1/users/calendars/${calWriteTarget[1]}/write-target`,
      kind: "passthrough",
      method: "PATCH",
    }
  }
  // CALFIX-CAL2 (2026-07-03): unlink a WHOLE connected account (every calendar row grouped
  // under it) in one call -- must be matched BEFORE the generic /users/calendars/{id} pattern
  // below (account_email may itself contain no slashes, e.g. "quentin@coowe.com", but this keeps
  // the two routes unambiguous regardless).
  const calAccount = p.match(/^\/users\/calendars\/account\/([^/]+)$/)
  if (calAccount && m === "delete") {
    return {
      devPath: `/v1/users/calendars/account/${calAccount[1]}`,
      kind: "passthrough",
      method: "DELETE",
    }
  }
  const cal = p.match(/^\/users\/calendars(?:\/([^/]+))?$/)
  if (cal) {
    if (m === "get") return { devPath: "/v1/users/calendars", kind: "passthrough" }
    if (m === "post" && !cal[1])
      return { devPath: "/v1/users/calendars", kind: "passthrough" }
    if (m === "delete" && cal[1])
      return { devPath: `/v1/users/calendars/${cal[1]}`, kind: "passthrough" }
  }

  // Wave 2 ICS calendar-feed subscribe URL: GET /users/me/calendar-feed-url -> dev
  // /v1/users/me/calendar-feed-url. Response is already {user_id, sig, url} -- no reshape needed.
  if (p === "/users/me/calendar-feed-url" && m === "get") {
    return { devPath: "/v1/users/me/calendar-feed-url", kind: "passthrough" }
  }

  // C1/C2 calendar-conflict data seam (2026-07-03), superseded by GAPI-MIGRATE (2026-07-03):
  // dev connects calendars SERVER-SIDE (no client gapi token, by design -- see calendarSyncUtils.js
  // GAPI-MIGRATE comments). Google events now come from the backend's Auth0-mediated
  // GET /v1/users/me/calendar-events (api/routers/calendars.py, CAL-CONFLICT), wired directly in
  // calendarSyncUtils.js fetchCalendarEvents -- route the beta-shaped call to it, passthrough.
  if (p === "/users/me/calendar-events" && m === "get") {
    return { devPath: "/v1/users/me/calendar-events", kind: "passthrough" }
  }

  // Wave4-P0FUNC ITEM2: GET /users/me/identities -> dev /v1/users/me/identities (real Auth0
  // identities, see api/routers/auth.py get_my_identities). Response {identities:[...]} already
  // matches the shape fetchDevIdentities()/updateUserIdentities() expects -- no reshape needed.
  if (p === "/users/me/identities" && m === "get") {
    return { devPath: "/v1/users/me/identities", kind: "passthrough" }
  }

  // Wave 3 WL2: legacy pre-versioned account-recovery-contact-method and connection-request paths.
  // Confirmed via QA-GREEN-WAVE2A-PROD (2026-07-02) these 404 on EVERY tier, including the real
  // beta backend on staging/prod -- not a dev-only gap. The dev backend has no equivalent route
  // (contacts.py only serves /v1/users/me/contacts, an unrelated directory-derived contact list;
  // there is no recovery-contact-method or connection-request concept on dev at all). getUserContact
  // Mechanisms/fetchRequests fire on nearly every page load (MePage/SettingsPage/ContactsPage/
  // TeeUpPage/CreateTeeUp/middleware bootstrap), so these are LIVE, high-frequency calls whose
  // errors are silently swallowed today -- not dead code, just a legacy backend feature nothing
  // currently serves on any tier. No /v1 repoint target exists, so per the shim-first-class pattern
  // used above (users/connections, users/matchings, users/interests, users/tags), safe-empty them
  // on dev rather than inventing a new backend route out of scope for this ticket.
  // Wave4-P0FUNC (QJ acceptance-spec pass): GET contact-mechanisms is REAL on dev now --
  // api/routers/auth.py get_my_contact_mechanisms serves the legacy cooe-schema data (recovery
  // phone/email, public phone/email/URL) in the exact {contactMechanisms:{email,phone,location,
  // url}} shape getUserContactMechanisms expects. Supersedes WL2's safe-empty stub for the GET.
  if (p === "/users/contact-mechanisms" && m === "get") {
    return { devPath: "/v1/users/me/contact-mechanisms", kind: "passthrough" }
  }
  // Multi-Identity Invitation Routing (spec gap closed 2026-07-03): the write path is REAL now
  // too -- add/remove a mechanism through api/routers/auth.py add_my_contact_mechanism /
  // remove_my_contact_mechanism, wired to the ALREADY-BUILT Settings > Invitations screen
  // (InvitationsDetailsContainer.js / InvitationsDetailsView.js -- the exact beta component QJ's
  // screenshot shows; it just had nothing real to call, WL2's stub 404'd every write). Response
  // shapes match what addUserContactMechanism/requestVerifyContactMechanism/
  // verifyContactMechanism expect (id/typeId/contactMechanismId/contactMechanismVerificationId).
  // Verification is DEV-SIMPLIFIED (documented in auth.py): a mechanism is marked verified at
  // add-time, and the verify-code stub accepts any code -- so the beta code-entry screen still
  // completes its normal flow without a real email/SMS send configured on this tier.
  if (p === "/users/contact-mechanisms" && m === "post") {
    return { devPath: "/v1/users/me/contact-mechanisms", kind: "passthrough" }
  }
  const cmVerifyCode = p.match(/^\/users\/contact-mechanisms\/verifications\/([^/]+)\/verify\/?$/)
  if (cmVerifyCode && m === "post") {
    return {
      devPath: `/v1/users/me/contact-mechanisms/verifications/${cmVerifyCode[1]}/verify`,
      kind: "passthrough",
    }
  }
  const cmVerifyReq = p.match(/^\/users\/contact-mechanisms\/([^/]+)\/verifications$/)
  if (cmVerifyReq && m === "post") {
    return {
      devPath: `/v1/users/me/contact-mechanisms/${cmVerifyReq[1]}/verifications`,
      kind: "passthrough",
    }
  }
  const cmId = p.match(/^\/users\/contact-mechanisms\/([^/]+)$/)
  if (cmId && m === "delete") {
    return { devPath: `/v1/users/me/contact-mechanisms/${cmId[1]}`, kind: "passthrough" }
  }
  if (
    p === "/users/contact-mechanisms" &&
    (m === "put" || m === "delete")
  ) {
    return { kind: "emptyObj" }
  }
  // /recovery and /login sub-paths (deleteRecoveryMechanism/deleteLoginMechanism) stay stubbed
  // -- unrelated to the Invitations screen (Account Recovery's single-slot rows), out of scope.
  if (
    /^\/users\/contact-mechanisms\/[^/]+\/(recovery|login)$/.test(p) &&
    (m === "delete" || m === "put" || m === "post")
  ) {
    return { kind: "emptyObj" }
  }
  if (p === "/users/requests" && m === "get") {
    return { kind: "emptyArr" }
  }
  if (p === "/users/requests" && m === "post") {
    return { kind: "emptyObj" }
  }
  if (/^\/users\/requests\/[^/]+$/.test(p)) {
    return { kind: "emptyObj" }
  }

  // AI-SLICE1 (2026-07-03): Create-TeeUp AI chat turn -> dev POST /v1/ai/create-draft (real Cloud
  // Ollama call, see api/routers/ai_create_draft.py). Response shape ({reply, patch,
  // matched_contact_ids}) already matches what CreateAiPage expects -- no reshape needed. The
  // caller (CreateAiPage/index.js) is responsible for the timeout/fallback contract (rule 17:
  // manual create must never depend on this call succeeding).
  if (p === "/users/ai/create-draft" && m === "post") {
    return { devPath: "/v1/ai/create-draft", kind: "passthrough", method: "POST" }
  }
  // CREATE-AI-V2 2026-07-04: connection-status ping for the rail header indicator (rule 5c) --
  // GET, no auth required server-side, cheap/fast (no LLM call), fine to call on every mount.
  if (p === "/users/ai/create-draft/health" && m === "get") {
    return { devPath: "/v1/ai/create-draft/health", kind: "passthrough", method: "GET" }
  }

  // AI-LIFECYCLE-WEB 2026-07-09: the floating Coowe AI panel (components/CooweAI/index.tsx) --
  // dev POST /v1/ai/coordination-chat, same passthrough shape as the create-draft turn above.
  // Response shape ({reply}) is a subset of create-draft's -- no reshape needed.
  if (p === "/users/ai/coordination-chat" && m === "post") {
    return { devPath: "/v1/ai/coordination-chat", kind: "passthrough", method: "POST" }
  }
  if (p === "/users/ai/coordination-chat/health" && m === "get") {
    return { devPath: "/v1/ai/coordination-chat/health", kind: "passthrough", method: "GET" }
  }

  return null
}

const SAFE_EMPTY = {
  emptyUsers: { users: {} },
  emptyGO: { gameplanOptions: {} },
  emptyObj: {},
  emptyArr: [],
  emptyTags: { data: [] },
  emptyContactMechanisms: { contactMechanisms: [] },
}

// ---- public: install on the axios client ----
export const installDevBackendShim = client => {
  if (!isDevBackend() || !client) return

  client.interceptors.request.use(config => {
    const route = routeBetaToDev(config.url, config.method)
    config.__shim = route || { kind: "passthrough" }
    // capture the inbox tab filter from the original (query-bearing) url for the list reshape
    if (route && route.kind === "list") {
      config.__shim = { ...route, filter: parseListFilter(config.url) }
    }

    // RELATE-CREATEAI-FIX 2026-07-06 (pre-existing bug, scoped fix): api.ts's createSession()
    // sets a GLOBAL axios default `Authorization: Bearer <opaque-dev-token>` header once the
    // user logs in. The dev backend never reads that header at all (its auth is entirely the
    // `?token=` query param set two lines below, see api/routers/auth.py get_user_id_from_token)
    // -- so the header is dead weight on dev builds. Worse, this origin sits behind a Caddy
    // HTTP-Basic-auth front door (dev-api-tls-frontdoor); once JS explicitly sets its own
    // Authorization header on a request, the browser will NOT also attach its cached front-door
    // Basic credentials for that request (a single request carries one Authorization header,
    // and an explicit JS-set value always wins over the browser's automatic cache) -- so Caddy's
    // basic_auth directive sees a `Bearer ...` value where it expects `Basic ...` and 401s the
    // request before it ever reaches the backend. Confirmed live: identical
    // GET /api/v1/users/me through the public front door succeeds (200) with no Authorization
    // header and fails (401) with one attached, even though the backend itself (bypassing Caddy)
    // returns 200 either way. Deleting the header here (dev-shim scope only, gated by
    // isDevBackend() at function entry -- never touches the prod/beta bearer-token auth path)
    // lets the browser's own cached front-door credentials go out on every dev-backend call.
    if (config.headers) {
      delete config.headers.Authorization
      delete config.headers.authorization
    }

    // attach dev token as ?token= on every request
    const token = getToken()
    if (token) config.params = { ...(config.params || {}), token }

    if (route && route.devPath) {
      config.url = route.devPath
      if (route.method) config.method = route.method
      // Wave1 FIX-1/INBOX-ARCH-001/002: forward the inbox tab's category to the dev backend so it
      // filters SERVER-SIDE (dev now supports ?category=1|2|3 on the list endpoint — verified live,
      // ?category=2 returns only the 3 archived objects). The beta FE only sets `category` in the
      // query for the Active (1) and Archive (2) tabs (filtersList in src/utils/inbox.ts); the
      // Invitations/Scheduled/Unscheduled/Ended tabs send no category at all, since they are
      // sub-views WITHIN the inbox — default those to "1" (INBOX) so they never leak archived/
      // trashed items. Without this the dev GET always returned every category mixed together and
      // relied entirely on the (now-removed) client-side category block in coMatchesTab.
      if (route.kind === "list") {
        const cat = config.__shim.filter && config.__shim.filter.category
        // WB3: also forward `search` on to the dev backend call (harmless if it ignores an
        // unknown param) -- the actual narrowing still happens client-side in reshapeList via
        // the captured filter, but the network call itself must carry the query param too.
        const search = config.__shim.filter && config.__shim.filter.search
        // Wave4-scale: for clean (non-client-sub-filtered) tabs, forward a real offset so
        // InfiniteScroll can page through the whole list (Archive can be thousands of rows). offset
        // = requestedPage * LIST_PAGE_SIZE, matching reshapeList's nextPage derivation. Sub-filtered
        // tabs keep offset 0 (single page) as before.
        const filt = config.__shim.filter || {}
        const clean = !listHasClientSubFilter(filt)
        const reqPage = clean ? filt.page || 0 : 0
        config.params = {
          ...(config.params || {}),
          category: cat || "1",
          ...(clean ? { offset: String(reqPage * LIST_PAGE_SIZE) } : {}),
          // Wave4-notif item 5: the FE's own limit/page (see prepareQueryParams, limit:15) were
          // baked into the ORIGINAL beta-shaped url as a querystring, which gets discarded above
          // when config.url is replaced with route.devPath -- so without this the dev backend saw
          // no limit/offset at all and fell back to its own default (200), silently truncating any
          // list past 200 items (QJ's real Archive tab has 2522). Forward the backend's own
          // documented max (500) so the fewest rows possible get dropped from `items` while
          // `total_count` (now used for the tab number, see reshapeList) stays authoritative
          // regardless. Wave4-scale UPDATE: true pagination is now implemented for clean tabs via
          // the `offset` param above + reshapeList's nextPage derivation, so InfiniteScroll walks
          // the whole list one LIST_PAGE_SIZE-row page at a time.
          limit: String(LIST_PAGE_SIZE),
          ...(search ? { search } : {}),
        }
      }
      // settings write: dev PUT /users/me/settings expects { settings: {...} }
      if (route.kind === "settingsWrite") {
        let b = config.data
        if (typeof b === "string") {
          try {
            b = JSON.parse(b)
          } catch {
            b = {}
          }
        }
        config.data = b && b.settings ? b : { settings: b || {} }
      }
      // WB2: translate the beta profile-edit body {name, avatar, backgroundImageUrl, ...} to the
      // dev PATCH /v1/users/me body {display_name, avatar_url, background_image_url}. The route
      // also accepts the beta field names directly, but translating here keeps the request body
      // self-describing and matches the response reshape below.
      if (route.kind === "profileWrite") {
        let b = config.data
        if (typeof b === "string") {
          try {
            b = JSON.parse(b)
          } catch {
            b = {}
          }
        }
        b = b || {}
        const out = {}
        if (b.name !== undefined) out.display_name = b.name
        if (b.avatar !== undefined) out.avatar_url = b.avatar
        if (b.backgroundImageUrl !== undefined)
          out.background_image_url = b.backgroundImageUrl
        // wave4-fix20: the profile edit form now writes the restored public-profile fields --
        // username/@handle + about/phone/location/linkedin/twitter/url/publicEmail (dev backend
        // persists the latter in users.preferences jsonb; username is a real column).
        if (b.nickName !== undefined) out.username = b.nickName
        if (b.username !== undefined) out.username = b.username
        if (b.about !== undefined) out.about = b.about
        if (b.phone !== undefined) out.phone = b.phone
        if (b.primaryLocation !== undefined) out.location = b.primaryLocation
        if (b.location !== undefined) out.location = b.location
        if (b.linkedin !== undefined) out.linkedin = b.linkedin
        if (b.twitter !== undefined) out.twitter = b.twitter
        if (b.url !== undefined) out.url = b.url
        if (b.publicEmail !== undefined) out.publicEmail = b.publicEmail
        // tz-persist 2026-07-05: settingsActions.js's changeUserTimeZone() already calls
        // updateProfile({ timezone }) -> PATCH /v1/users/me on every Settings > Time Zone
        // change, but this translator's explicit allowlist never forwarded `timezone` into
        // `out`, so the shim silently dropped it on the floor before it ever reached the
        // dev backend -- the PATCH round-tripped 200 with no actual write. The backend route
        // now also accepts+persists `timezone` (writes the real users.timezone column, see
        // api/routers/auth.py); forward it through here so the two sides actually connect.
        if (b.timezone !== undefined) out.timezone = b.timezone
        config.data = out
      }
    } else if (route && route.kind === "createTeeup") {
      // CREATE + SEED (mirrors the iOS create shim). The beta create POSTs `/teeups` with
      //   { name, photo, invited:[...], gameplanOptions:[{type:'when'|'where', suggestions:[...]}] }
      // and the old API created the CO AND its initial When/Where suggestions in ONE call. The dev
      // backend splits this: POST /coordination-objects creates the CO and AUTO-SEEDS empty When +
      // Where gameplan options (asynchronously — they appear on a follow-up GET, not in the create
      // response). Each create-time suggestion must then be attached to its dimension's seeded
      // option. The old shim dropped gameplanOptions entirely, so a When/Where chosen during create
      // never persisted (suggest-during-create lost its rows). This adapter does the full sequence.
      config.adapter = async cfg => {
        let b = cfg.data
        if (typeof b === "string") {
          try {
            b = JSON.parse(b)
          } catch {
            b = {}
          }
        }
        b = b || {}
        const tok = getToken()
        const dev = (method, path, body) =>
          fetch(`${API_BASE}${path}?token=${encodeURIComponent(tok)}`, {
            method,
            headers: { "Content-Type": "application/json" },
            body: body ? JSON.stringify(body) : undefined,
          }).then(r => (r.ok ? (r.status === 204 ? null : r.json()) : null))

        // 1) create the CO. The create modal may hand back a base64 `data:` URI for the cover
        // (an unsplash/upload preview) — never store that as image_url; only pass a real http(s) URL.
        const rawPhoto = b.photo || b.image || b.imageUrl || null
        const coverUrl =
          typeof rawPhoto === "string" && /^https?:\/\//i.test(rawPhoto)
            ? rawPhoto
            : null
        const created = await dev("POST", "/v1/coordination-objects", {
          type: "teeup",
          name: b.name || "New TeeUp",
          description: b.description || "",
          coordination_purpose: null,
          org_id: "11111111-1111-1111-1111-111111111111",
          image_url: coverUrl,
        })
        const coId = created && created.id
        // 2) read back the seeded When/Where option UUIDs (auto-seed is async — poll briefly)
        const optByDim = {}
        if (coId) {
          for (let attempt = 0; attempt < 6; attempt++) {
            const detail = await dev(
              "GET",
              `/v1/coordination-objects/${coId}`
            )
            ;(detail?.gameplan || []).forEach(g => {
              optByDim[g.dimension] = g.id
            })
            if (optByDim.when || optByDim.where) break
            await new Promise(r => setTimeout(r, 350))
          }
          // 3) attach each create-time suggestion to its dimension's seeded option
          const buildCreateValue = (s, isWhen) =>
            isWhen
              ? {
                  start: toMs(s.startDate) != null ? s.startDate : s.start,
                  end: s.endDate || s.startDate || s.end,
                  custom_date: s.customDate || "",
                  custom_time: s.customTime || "",
                  timezone: !!s.isTimeZoneEnabled,
                  type: "when",
                }
              : {
                  name: s.value || s.name || "",
                  details: s.details || s.fullDetails || "",
                  url: s.urlAddress || s.url || "",
                  url_type: s.urlType,
                  location_type: s.locationType,
                  latitude: s.latitude != null ? s.latitude : s.lat,
                  longitude: s.longitude != null ? s.longitude : s.lng,
                  google_place_id: s.googlePlaceId,
                }
          const gpOptions = Array.isArray(b.gameplanOptions)
            ? b.gameplanOptions
            : []
          for (const gp of gpOptions) {
            const dim =
              gp.type === "when" ? "when" : gp.type === "where" ? "where" : null
            const optUuid = dim && optByDim[dim]
            if (!optUuid) continue
            for (const s of gp.suggestions || []) {
              const isWhen = dim === "when"
              const r = await dev(
                "POST",
                `/v1/coordination-objects/${coId}/suggestions`,
                {
                  gameplan_option_id: optUuid,
                  value: buildCreateValue(s, isWhen),
                }
              )
              if ((s.isDecided || s.decided) && r && r.id) {
                await dev(
                  "POST",
                  `/v1/coordination-objects/${coId}/suggestions/${r.id}/decide`,
                  {}
                )
              }
            }
          }
          // 4) invitees -> one participant add each
          const invited = Array.isArray(b.invited) ? b.invited : []
          for (const iv of invited) {
            const uid = unaliasId(iv.id || iv.userId)
            if (uid) {
              await dev(
                "POST",
                `/v1/coordination-objects/${coId}/participants`,
                { user_id: uid, role: "participant" }
              )
            } else if (iv.email || iv.phone) {
              // A11.2 EXTENSION (2026-07-08): the CREATE path counterpart of WL4-1's fix on the
              // existing-TeeUp invite adapter (route.kind === "invite" below). formatInvitees()
              // reshapes a MANUAL invitee to {email|phone, firstName, lastName} with NO id, so
              // this loop previously skipped it silently and a bare-email invitee added at
              // create time (e.g. People-page shadow selection) never reached the backend.
              // Same first-class non-user contract: POST participants {channel, value} records
              // an idempotent pending_invites row (verified live by WL4-1 on this backend).
              await dev(
                "POST",
                `/v1/coordination-objects/${coId}/participants`,
                {
                  channel: iv.phone ? "phone" : "email",
                  value: iv.email || iv.phone,
                  role: "participant",
                }
              )
            }
          }
        }
        const aliasTid = aliasId(coId)
        return {
          data: {
            teeupId: aliasTid,
            id: aliasTid,
            teeupUrl: "",
            name: (created && created.name) || b.name || "New TeeUp",
            status: 1,
            userstatus: 2,
            createdat: new Date().toISOString(),
            updatedat: new Date().toISOString(),
          },
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        }
      }
    } else if (route && route.kind === "userStatusChange") {
      // RSVP change (BUG-049): the beta PUTs { userId, statusId } where statusId is the numeric
      // beta user-status code. Map it to the dev RSVP route (same one the inbox list-socket uses).
      config.adapter = async cfg => {
        let b = cfg.data
        if (typeof b === "string") {
          try {
            b = JSON.parse(b)
          } catch {
            b = {}
          }
        }
        b = b || {}
        const statusName =
          USER_STATUS_NAME[b.statusId] || USER_STATUS_NAME[b.status] || "going"
        const uuid = unaliasId(route.teeupId)
        const tok = getToken()
        await fetch(
          `${API_BASE}/v1/coordination-objects/${uuid}/participants/me/status?token=${encodeURIComponent(
            tok
          )}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: statusName, status_context: null }),
          }
        ).catch(() => {})
        return {
          data: { ok: true, userStatus: statusName },
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        }
      }
    } else if (route && route.kind === "categoryChange") {
      // Wave1 FIX-2: the real participant-category endpoint now exists, so Dismiss/Decline/Archive
      // sets the ACTUAL category (1=INBOX, 2=ARCHIVE, 3=TRASH) instead of the old not_going
      // workaround (BUG-RCNEXT-WEB-002, written when dev had no category route). Reversible via
      // category=1 restore; participant row preserved (no destructive DELETE).
      config.adapter = async cfg => {
        const uuid = unaliasId(route.teeupId)
        const tok = getToken()
        await fetch(
          `${API_BASE}/v1/coordination-objects/${uuid}/participants/me/category?token=${encodeURIComponent(
            tok
          )}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ category: route.categoryId }),
          }
        ).catch(() => {})
        return {
          data: { ok: true },
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        }
      }
    } else if (route && route.kind === "invite") {
      // 1 beta call ({invited:[...]}) -> N dev participant adds.
      // WL1: manual (unresolved-email/phone) invitees were unconditionally sent as
      // {user_id: unaliasId(iv.id||iv.userId), role}. A manual chip's `id` is a client-synthesized
      // negative int (contactUtils.prepareManualContact) that never appears in the alias table, so
      // unaliasId() returned it unchanged -- a negative number posted into a `user_id: UUID` field,
      // which the backend's AddParticipant model rejects (422/400: not a valid UUID). The backend
      // ALREADY has a first-class non-CooWe-user invite contract for exactly this case --
      // POST .../participants {channel:'email'|'phone', value} -- which records a pending_invites
      // row (idempotent, status:"pending") instead of a participants row. Route manual/unresolved
      // invitees through channel+value and only resolved CooWe users (a real aliasable id) through
      // user_id, so a non-existent-user email invite attaches as a pending invite instead of
      // silently failing (this adapter previously swallowed the error and reported success anyway --
      // the "ghost" bug class flagged in Wave 1 lessons).
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      config.adapter = async cfg => {
        let b = cfg.data
        if (typeof b === "string") {
          try {
            b = JSON.parse(b)
          } catch {
            b = {}
          }
        }
        const invited = (b && b.invited) || []
        const uuid = unaliasId(route.teeupId)
        const tok = getToken()
        const results = await Promise.all(
          invited.map(iv => {
            const resolvedId = unaliasId(iv.id != null ? iv.id : iv.userId)
            const isResolvedUser = UUID_RE.test(String(resolvedId))
            let payload
            if (isResolvedUser) {
              payload = { user_id: resolvedId, role: "participant" }
            } else if (iv.value && iv.contactType) {
              payload = {
                channel: iv.contactType === "phone" ? "phone" : "email",
                value: iv.value,
                role: "participant",
              }
            } else if (iv.email || iv.phone) {
              // WL4-1: the EXISTING-TeeUp invite path runs manual chips through
              // contactUtils.formatInvitees() BEFORE this adapter, which reshapes a MANUAL
              // invitee to {email|phone, firstName, lastName} (the beta wire format) -- no
              // `value`/`contactType` and no id. This branch was missing, so every manually
              // typed email/phone invitee on an existing TeeUp was silently `skipped` here:
              // ZERO fetches fired (the QA-observed "Send fires no requests" bug), while the
              // adapter still reported partial success upstream.
              payload = {
                channel: iv.phone ? "phone" : "email",
                value: iv.email || iv.phone,
                role: "participant",
              }
            } else {
              // Neither a resolved CooWe user nor a manual value/contactType/email/phone
              // shape -- nothing sendable, skip rather than post a guaranteed-invalid user_id.
              return Promise.resolve({ ok: false, skipped: true, iv })
            }
            return fetch(
              `${API_BASE}/v1/coordination-objects/${uuid}/participants?token=${encodeURIComponent(
                tok
              )}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              }
            )
              .then(r => ({ ok: r.ok, status: r.status, iv }))
              .catch(() => ({ ok: false, iv }))
          })
        )
        const failed = results.filter(r => !r.ok)
        return {
          data: {
            invited: invited.length,
            succeeded: results.length - failed.length,
            failed: failed.length,
            status: failed.length ? "partial" : "ok",
          },
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        }
      }
    } else if (route && SAFE_EMPTY[route.kind] !== undefined) {
      // short-circuit: resolve a safe-empty body without hitting the network
      config.adapter = cfg =>
        Promise.resolve({
          data: SAFE_EMPTY[route.kind],
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        })
    } else if (route && route.kind === "tokenRefresh") {
      config.adapter = cfg =>
        Promise.resolve({
          data: { accessToken: token, refreshToken: token },
          status: 200,
          statusText: "OK",
          headers: {},
          config: cfg,
          request: {},
        })
    }
    return config
  })

  client.interceptors.response.use(async response => {
    // Scrub AWS image URLs from EVERY response (covers/avatars/attachments) before anything else,
    // so no *.amazonaws.com URL ever reaches the FE — even on passthrough calls and fields the
    // reshapers don't touch. Reshaping below then copies the already-rewritten (media-host) URLs.
    if (response && response.data) response.data = deAwsDeep(response.data)
    const kind = response.config?.__shim?.kind
    if (!kind || kind === "passthrough") return response
    try {
      if (kind === "list")
        response.data = reshapeList(response.data, response.config.__shim?.filter)
      else if (kind === "me") response.data = reshapeMe(response.data)
      // WB2: the PATCH response is the same user shape GET returns (minus timezone/settings,
      // which this route doesn't touch) -- reuse reshapeMe so the client sees the identical
      // field names (name/avatar/username) it already renders from after GET /users.
      else if (kind === "profileWrite") response.data = reshapeMe(response.data)
      else if (kind === "detail") response.data = reshapeDetail(response.data)
      // CREATE-AI-POLISH 2026-07-03: fetchTeeupUsersById destructures `{ users }` off the
      // response body -- reuse the same devParticipantToBetaUser mapping reshapeDetail uses so
      // the shape (id/username/avatar/isOrganiser/...) is identical to every other users list.
      else if (kind === "teeupUsers")
        response.data = {
          users: (response.data.participants || []).map(devParticipantToBetaUser),
        }
      else if (kind === "gameplanOptions")
        // fetchGameplanOptions expects response.data = the gameplan-options array (same shape
        // reshapeDetail puts on activeTeeup.gameplans).
        response.data = reshapeGameplan(
          response.data.gameplan,
          response.data.participants
        )
      else if (kind === "messages") {
        const beta = reshapeMessages(response.data)
        const coId = response.config.__shim?.coId
        if (coId) {
          const sugMsgs = await fetchSuggestionMessagesForCo(coId)
          if (sugMsgs.length) beta.messages = [...beta.messages, ...sugMsgs]
        }
        response.data = beta
      }
      else if (kind === "profile") response.data = reshapeProfile(response.data)
      else if (kind === "contacts") response.data = reshapeContacts(response.data)
      else if (kind === "directory") response.data = reshapeDirectory(response.data)
      else if (kind === "inviteeSuggestions")
        response.data = reshapeInviteeSuggestions(response.data)
      else if (kind === "notifications") {
        // wave4-notif-conform: dev backend now returns {items, has_more, offset, limit} (paginated).
        // Preserve that shape (notificationsApi reads .items/.has_more) while keeping the legacy
        // aliases for any older consumer.
        const raw = response.data
        const arr = Array.isArray(raw)
          ? raw
          : raw?.items || raw?.notifications || raw?.data || []
        response.data = {
          items: arr,
          has_more: Array.isArray(raw) ? false : !!raw?.has_more,
          data: arr,
          notifications: arr,
        }
      } else if (kind === "settingsWrite") {
        // dev returns the saved settings; the FE mostly ignores the body
        response.data = response.data || { ok: true }
      } else if (kind === "createTeeup") {
        // The create adapter already returns the final beta shape ({ teeupId, id, ... }). Only
        // reshape if some non-adapter path produced a raw dev CO (leave the adapter result intact;
        // overwriting it would drop teeupId, which createTeeupActions reads).
        const d = response.data || {}
        if (d.teeupId == null) {
          response.data = {
            teeupId: aliasId(d.id),
            id: aliasId(d.id),
            name: d.name || "New TeeUp",
            status: teeupStatusCode(d.status || "planning"),
            userstatus: 2,
            createdat: new Date().toISOString(),
            updatedat: new Date().toISOString(),
          }
        }
      }
    } catch (e) {
      // never let a reshape error blank the app
      // eslint-disable-next-line no-console
      console.warn("[devBackendShim] reshape failed", kind, e)
    }
    return response
  })
}

// ---- socket -> REST shim ----------------------------------------------------------------------
// The real beta sends chat/react/suggest/status/nudge over a websocket (chat.coo-e.com); there's no
// dev socket server. This shim replaces the per-teeup socket on the dev deploy: each write emit() is
// translated to the dev backend's REST endpoint, then the matching on() handler is fired with the
// server-echo shape so the UI updates exactly as it would from a socket broadcast. Single-user
// (no live receive-from-others — that needs a real dev socket server).
const REACTION_NAME = {
  1: "love",
  2: "upvote",
  3: "downvote",
  4: "neutral",
  5: "notVote",
  6: "x",
  7: "question",
  8: "check",
}
const TEEUP_STATUS_NAME = {
  1: "planning",
  2: "allset",
  3: "happening",
  4: "ended",
  5: "cancelled",
  6: "happeningSoon",
}

export const makeDevTeeupSocket = ({ token, teeupId, userId }) => {
  const handlers = {}
  const fire = (event, ...args) =>
    (handlers[event] || []).forEach(h => {
      try {
        h(...args)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[devSocket]", event, e)
      }
    })
  const uuid = () => unaliasId(teeupId)

  // B8 — LIVE CROSS-USER PUSH. Own writes still persist + render locally via the REST path below
  // (unchanged). Additionally open a REAL socket.io connection to the dev realtime server so that
  // (a) after each local write we emit "notify" and the server relays a "teeup:sync" to OTHER users
  // in this teeup's room, and (b) when ANOTHER user writes we receive "teeup:sync" and the
  // middleware refetches this teeup (alias-safe, exact beta shapes). Pure-additive: if the realtime
  // server is unreachable, own writes still work exactly as before.
  let rs = null
  try {
    const SOCKET_URL = process.env.REACT_APP_SOCKET_ENDPOINT
    if (SOCKET_URL) {
      // eslint-disable-next-line global-require
      const io = require("socket.io-client")
      rs = io.connect(SOCKET_URL, {
        transports: ["websocket"],
        forceNew: true,
        // chain-3 U-N3a-1 FIX: was `user_id: token` -- the server (realtime/server.js) joins this
        // connection to `user:{user_id}` verbatim off the query string, and every user-room push
        // (teeup:new/invitation/skip/archive) is addressed by the REAL user id, never the token.
        // With the token in that slot this connection silently never received a single user-room
        // push. `userId` is already a real param of this function -- use it.
        query: { token, teeup_id: uuid(), user_id: userId },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      })
      // server -> this client: another user changed this teeup; surface to the middleware handler
      rs.on("teeup:sync", p => fire("teeup:sync", p))
      // chain-3 U-N3a-1: these 5 raw relay events are payload-echo-only by server design (no
      // teeup:sync companion -- see realtime/server.js's pinboard / pinboard:suggestion /
      // suggestion / suggestions handlers, and teeup:skip which the server pushes to this same
      // connection's user-room on kick/leave) so they were never reaching the middleware's
      // existing (until now dead -- registered via teeupSocket.on(...) but never invoked)
      // listeners. Forward each verbatim.
      ;["pinboard", "pinboard:suggestion", "suggestion", "suggestions", "teeup:skip"].forEach(
        evt => rs.on(evt, (...args) => fire(evt, ...args))
      )
      rs.on("connect_error", e =>
        // eslint-disable-next-line no-console
        console.warn("[devSocket] realtime connect_error", e && e.message)
      )
      // chain-3 U-N3a-1: refresh-fallback -- a reconnect means some window of pushes may have been
      // missed (the relay does not replay backlog); force a full refetch so state never drifts
      // stale beyond one reconnect cycle.
      rs.on("reconnect", () => fire("teeup:sync", { reason: "reconnect" }))
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[devSocket] realtime init failed", e && e.message)
  }
  const notify = kind => {
    try {
      if (rs) rs.emit("notify", { kind })
    } catch (e) {
      /* ignore */
    }
  }
  // Every write goes through one fetch with an 8s AbortController timeout (BUG: suggest spins
  // forever on a bad connection — e.g. plane wifi — because the REST write never resolves). The
  // timeout converts an indefinite hang into a thrown error the emit() catch handles, so the UI
  // settles (the optimistic item stays, the write retries on the next action) instead of spinning.
  const req = async (method, path, body) => {
    const sep = path.indexOf("?") === -1 ? "?" : "&"
    const ctrl = new AbortController()
    const t = setTimeout(() => ctrl.abort(), 8000)
    try {
      const res = await fetch(`${API_BASE}${path}${sep}token=${encodeURIComponent(token)}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
        signal: ctrl.signal,
      })
      if (!res.ok) {
        // FIX 2026-07-04: capture the backend's JSON `detail` so callers (e.g. the statusChange
        // 400-surfacing below) can show a real message instead of just a status code.
        let detail = ""
        try {
          detail = (await res.json())?.detail || ""
        } catch {
          /* non-json */
        }
        const err = new Error(
          detail ? `${method} ${path} -> ${res.status}: ${detail}` : `${method} ${path} -> ${res.status}`
        )
        err.status = res.status
        err.detail = detail
        throw err
      }
      return res.status === 204 ? null : res.json()
    } finally {
      clearTimeout(t)
    }
  }
  const nowIso = () => new Date().toISOString()

  const socket = {
    connected: true,
    disconnected: false,
    io: { on: () => {}, off: () => {} },
    on: (e, cb) => {
      ;(handlers[e] = handlers[e] || []).push(cb)
      return socket
    },
    once: (e, cb) => socket.on(e, cb),
    off: () => socket,
    removeListener: () => socket,
    removeAllListeners: () => socket,
    open: () => socket,
    connect: () => socket,
    close: () => socket,
    disconnect: () => socket,
    emit: async (event, payload = {}, meta) => {
      try {
        const id = uuid()
        if (event === "teeup:message") {
          // mentionIds/targetId arrive as the FE's int aliases; the backend keys on dev UUIDs,
          // so unalias before sending (else mention/nudge targeting silently no-ops).
          const mentionUuids = (payload.mentionIds || [])
            .map(unaliasId)
            .filter(Boolean)
          // Nudge: the beta sends a nudge as teeup:message with messageTypeId=nudge(5) plus the
          // nudged user(s) in mentionIds/targetId and the prompt in subTypeId. Route it to the
          // dev nudge endpoint so the backend fires a TARGETED nudge (a plain /messages POST
          // would lose the target + sound).
          const isNudge =
            payload.messageTypeId === 5 ||
            payload.targetId != null ||
            (payload.subTypeId != null && Array.isArray(payload.mentionIds))
          if (isNudge) {
            const targets = mentionUuids.slice()
            if (payload.targetId != null) {
              const t = unaliasId(payload.targetId)
              if (t && !targets.includes(t)) targets.push(t)
            }
            if (targets.length) {
              await req("POST", `/v1/coordination-objects/${id}/nudge`, {
                target_user_ids: targets,
                text: payload.text || "Are you going?",
                sub_type: payload.subTypeId != null ? String(payload.subTypeId) : null,
              })
            }
            return
          }
          const r = await req("POST", `/v1/coordination-objects/${id}/messages`, {
            type: "text",
            body: {
              text: payload.text || "",
              segments: payload.text ? [payload.text] : [],
              mentionIds: mentionUuids,
            },
          })
          fire("teeup:message", devMessageToBeta({ ...r, user_id: userId }), {
            teeupId,
          })
        } else if (event === "reply") {
          const r = await req("POST", `/v1/coordination-objects/${id}/messages`, {
            type: "text",
            body: { text: payload.text || "", segments: [payload.text || ""] },
            parent_id: unaliasId(payload.messageId),
            parent_type: "message",
          })
          fire(
            "reply",
            { ...devMessageToBeta({ ...r, user_id: userId }), messageId: payload.messageId },
            { teeupId }
          )
        } else if (event === "comment") {
          const r = await req("POST", `/v1/coordination-objects/${id}/messages`, {
            type: "text",
            body: { text: payload.text || "", segments: [payload.text || ""] },
            suggestion_id: unaliasId(payload.suggestionId),
          })
          fire("comment", devMessageToBeta({ ...r, user_id: userId }), { teeupId })
        } else if (event === "statusChange") {
          // teeup-level STAGE change -> the organiser-gated stage endpoint (was a raw PATCH, which
          // bypassed the organiser gate + transition validation). POST /status maps the beta status
          // code -> the dev stage name and enforces planning->all_set->happening_soon->... forward.
          const statusName = TEEUP_STATUS_NAME[payload.status]
          if (statusName) {
            await req("POST", `/v1/coordination-objects/${id}/status`, { status: statusName })
            fire(
              "statusChange",
              {
                changeFor: "teeup",
                statusId: payload.status,
                updatedBy: userId,
                senderId: userId,
                createdAt: nowIso(),
              },
              { teeupId }
            )
          }
        } else if (event === "reaction") {
          const reactionName = REACTION_NAME[payload.reactionId] || "upvote"
          await req(
            "PUT",
            `/v1/coordination-objects/${id}/suggestions/${unaliasId(payload.suggestionId)}/react`,
            { reaction: reactionName }
          )
          fire(
            "suggestion:react",
            {
              reaction: {
                reactionId: payload.reactionId,
                suggestionId: payload.suggestionId,
                updatedBy: userId,
                senderId: userId,
                typeId: 3,
                createdAt: nowIso(),
              },
            },
            { teeupId }
          )
        } else if (event === "reaction:remove") {
          // BUG-020 / (11-R1b): a 2nd tap on a reaction REMOVES it. Delete the reaction on the dev
          // backend, then echo "suggestion:react" with typeId = retract (5) so the reducer's
          // CONFIRM_REACTION path (isRetraction = typeId === statusTypeIds.retract) drops the user
          // from reactedBy.
          //
          // R1b fix: this used to `.catch(() => {})` the DELETE and fire the retraction
          // UNCONDITIONALLY regardless of outcome -- so the toggle visually unfilled even when the
          // write silently failed. Confirmed live against app-dev.coowe.com (curl, 2026-07-03):
          // DELETE .../suggestions/{id}/react returns a flat 405 Method Not Allowed (OPTIONS on the
          // same path reports `Allow: POST` only) -- this dev-backend endpoint has no working
          // removal route at all (PUT/POST are pure upserts: re-PUTting the same reactionId does
          // NOT toggle it off, unlike the real teeup-api contract's PUT-toggles-to-retract
          // semantics -- src/routes/teeups/suggestions/reactions.ts). Do NOT swallow the failure
          // and confirm removal that didn't happen -- let it throw into the outer catch (same as
          // the "reaction" add branch above, which already does not swallow), so the UI honestly
          // stays filled when the backend rejects the write instead of lying that it succeeded.
          await req(
            "DELETE",
            `/v1/coordination-objects/${id}/suggestions/${unaliasId(
              payload.suggestionId
            )}/react`
          )
          fire(
            "suggestion:react",
            {
              reaction: {
                reactionId: payload.reactionId,
                suggestionId: payload.suggestionId,
                updatedBy: userId,
                senderId: userId,
                typeId: 5,
                isRetraction: true,
                createdAt: nowIso(),
              },
            },
            { teeupId }
          )
        } else if (event === "suggestion" || event === "suggestions") {
          // formatSuggestionOut emits a flat payload; the dev backend stores `value` as a jsonb
          // dict that devSuggestionToBeta later reads back as value.start/value.end (when) or
          // value.name/details (where). Build that shape explicitly — dumping the whole flat
          // payload as `value` did not round-trip (suggestions rendered blank after reload).
          // NOTE: the SuggestWhen/Where modals emit "suggestions" (PLURAL, an array) via
          // createSuggestions(); the single "suggestion" event is a separate (legacy) path. Handle
          // both — without the plural branch the Suggest button was a silent no-op (no POST).
          const buildValue = pl => {
            const isWhen = pl.startDate != null || pl.endDate != null || pl.type === "when"
            return isWhen
              ? {
                  start: pl.startDate,
                  end: pl.endDate || pl.startDate,
                  custom_date: pl.customDate || "",
                  custom_time: pl.customTime || "",
                  timezone: !!pl.isTimeZoneEnabled,
                }
              : {
                  name: pl.value || pl.name || "",
                  details: pl.details || pl.fullDetails || "",
                  url: pl.urlAddress || pl.url || pl.value || "",
                  url_type: pl.urlType,
                  location_type: pl.locationType,
                  latitude: pl.latitude != null ? pl.latitude : pl.lat,
                  longitude: pl.longitude != null ? pl.longitude : pl.lng,
                  google_place_id: pl.googlePlaceId,
                }
          }
          const items = Array.isArray(payload) ? payload : [payload]
          for (const item of items) {
            const optionId = unaliasId(item.optionId || item.gameplanOptionId)
            const r = await req("POST", `/v1/coordination-objects/${id}/suggestions`, {
              gameplan_option_id: optionId,
              value: buildValue(item),
            })
            // "Decide on this" stages a suggestion with isDecided:true and emits it through the
            // same createSuggestions path; honor it by deciding the just-created suggestion (the
            // backend create endpoint does not read isDecided).
            if ((item.isDecided || item.decided) && r && r.id) {
              await req(
                "POST",
                `/v1/coordination-objects/${id}/suggestions/${r.id}/decide`,
                {}
              ).catch(() => {})
            }
            fire("suggestion", { ...r, teeupId, senderId: userId }, { teeupId })
          }
        } else if (event === "decide") {
          // UNDECIDE PARITY (mirrors the iOS request.js fix): the web undecide path
          // (usePopupActions) emits "decide" with isDecided:false. Mapping that to POST /decide
          // re-decided the suggestion (no-op / wrong) so a decided When/Where could never be
          // reverted. Route isDecided===false to the backend /undecide route; isDecided!==false
          // still decides.
          const undecide = payload.isDecided === false
          await req(
            "POST",
            `/v1/coordination-objects/${id}/suggestions/${unaliasId(payload.suggestionId)}/${
              undecide ? "undecide" : "decide"
            }`,
            {}
          )
          fire(
            "decide",
            {
              suggestionId: payload.suggestionId,
              isDecided: payload.isDecided,
              updatedBy: userId,
              senderId: userId,
            },
            { teeupId }
          )
          // DEAD-SOCKET BACKSTOP (mirrors the iOS REFRESH_TEEUP backstop): on the dev backend
          // there is no live echo carrying the post-decide gameplanOptions, and the keyed-store
          // refresh does not rebuild activeTeeup.gameplans (which the When/Where row reads). Trigger
          // a full local teeup refetch so the row re-renders with the now-correct decided state.
          fire("teeup:sync", { reason: "decide" })
        } else if (event === "suggestion:removeFromGamePlan") {
          // UNDECIDE COMPANION — NOT a delete. usePopupActions' undecide fires BOTH
          // removeFromGameplan (this event) AND markAsDecided(id,false) (the "decide" event above,
          // which now hits /undecide). On dev this emit must be a NO-OP: the /undecide call does the
          // real work. Deleting here destroyed the suggestion on every undecide. Echo the un-select
          // so the optimistic store state matches, but make no backend write.
          fire(
            "suggestion:removeFromGamePlan",
            { suggestion: { id: payload.suggestionId, isSelected: false }, senderId: userId },
            { teeupId }
          )
        } else if (event === "suggestions/withdraw") {
          await req(
            "DELETE",
            `/v1/coordination-objects/${id}/suggestions/${unaliasId(payload.suggestionId)}`
          )
          fire(
            "suggestions/withdraw",
            {
              suggestionId: payload.suggestionId,
              isWithdrawn:
                payload.isWithdrawn !== undefined ? payload.isWithdrawn : true,
              senderId: userId,
            },
            { teeupId }
          )
        } else if (event === "pinboard") {
          // Add/Remove-to-Pinboard from the MessageBar (useMessageBarActions) / Conversation
          // -- pinUnpinMessage emits this. Route to the dedicated pin route (deployed alongside
          // this fix) rather than a generic message PATCH -- mirrors the reaction/decide
          // branches above: local REST write, then echo the same "pinboard" shape the client
          // already listens for at backendMiddleware.js's teeupSocket.on("pinboard", ...)
          // so the conversation "Pinned" filter updates without a full refetch.
          await req(
            "POST",
            `/v1/coordination-objects/${id}/messages/${payload.messageId}/pin`,
            { isPinned: !!payload.isPinned }
          )
          fire(
            "pinboard",
            {
              messageId: payload.messageId,
              isPinned: !!payload.isPinned,
              pinnedById: payload.isPinned ? userId : null,
            },
            { teeupId }
          )
        } else if (event === "pinboard:suggestion") {
          // Add/Remove-to-Pinboard from the SuggestionPopup (usePopupActions) / TeeUpsOverview
          // MessageTooltip -- pinUnpinSuggestion emits this. Route to the dedicated pin route
          // (deployed alongside this fix) rather than a generic suggestion PATCH -- mirrors the
          // reaction/decide branches above: local REST write, then echo the same
          // "pinboard:suggestion" shape the client already listens for at backendMiddleware.js's
          // teeupSocket.on("pinboard:suggestion", ...) so the popup and the Conversation "Pinned"
          // filter update without a full refetch.
          await req(
            "POST",
            `/v1/coordination-objects/${id}/suggestions/${unaliasId(
              payload.suggestionId
            )}/pin`,
            { isPinned: !!payload.isPinned }
          )
          fire(
            "pinboard:suggestion",
            {
              suggestionId: payload.suggestionId,
              isPinned: !!payload.isPinned,
              pinnedById: payload.isPinned ? userId : null,
            },
            { teeupId }
          )
        } else if (event === "teeup:messageReaction") {
          // Add/Remove a reaction to a message from the MessageBar (useMessageBarActions)
          // Route to the dedicated reaction route (deployed alongside this fix) rather than
          // a generic message PATCH -- mirrors the reaction/decide branches above: local REST write,
          // then trigger a sync refetch to update the reactions array.
          await req("POST", `/v1/coordination-objects/${id}/messages/${payload.messageId}/reactions`, { reaction: payload.reaction })
          fire("teeup:sync", { reason: "messageReaction" })
        }
        // After a successful local write, tell the realtime server to push a live "teeup:sync" to
        // the OTHER users in this teeup's room (cross-user receive). Own UI already updated above.
        notify(event)
      } catch (e) {
        // BUG-047: the dev backend can answer a write that DID commit with a 422 (the false
        // "HTTP 422 Unprocessable Entity" toast on a successful send). Never surface that as an
        // error: when the failure is a 422, reconcile by refetching the teeup so the committed
        // change appears locally. Any other error still just logs (no user-facing toast on web).
        const msg = (e && e.message) || ""
        if (msg.indexOf("422") !== -1) {
          try {
            if (rs) rs.emit("notify", { kind: event })
          } catch (_) {
            /* ignore */
          }
          fire("teeup:sync", { reason: "soft-422" })
        }
        // FIX 2026-07-04 (blocked stage changes toast instead of silent no-op -- BACKSTOP): the
        // real pre-flight block + canonical toast lives in DropDown/index.tsx's changeStatus
        // (fires before this request is ever made, matching mobile's pre-flight pattern). This
        // covers any other path that still reaches the backend's terminal-stage 400
        // (coordination_objects.py:736-737 / lifecycle.py:19). Prefer the same canonical iPhone
        // strings (isAlreadyEndedWarning / isAlreadyCancelledWarning, src/i18n/en.js) when the
        // backend detail matches the ended/cancelled rejection; fall back to the raw backend
        // detail for anything unrecognized so a blocked change is still visible, never a silent
        // no-op. confirmAlert (react-confirm-alert) is used here rather than Toast.show because
        // this module has no React tree to render Toast's (stub, non-functional on web anyway).
        //
        // FIX 2026-07-11 (chain-3 U-W8 item 3 / U-B2 finding 18): the block above only fired for
        // event === "statusChange" -- every OTHER terminal-guard rejection (U-B2 closed 6 new gaps
        // + the 13 pre-existing require_active/require_chat_open call sites: message send, react,
        // un-react, suggest, decide, undecide, pin, nudge, RSVP, invite, rename, task-create) fell
        // straight through to the bare console.warn below with ZERO on-screen feedback -- a blocked
        // write looked identical to a successful no-op. Generalize the same canonical-toast
        // backstop to every event, keyed off the ACTUAL rejection shapes (not the event name):
        //   - require_active (auth.py:1310): 409 {"detail":"This TeeUp has {cancelled|ended} and
        //     can no longer be changed"}.
        //   - require_chat_open (auth.py:1330, messages.py): 409, three message-specific variants
        //     ("...was cancelled; its conversation is closed" / "...ended; its conversation is now
        //     closed"); the within-grace-window ended case is 200 (not an error, never reaches here).
        //   - change_stage's own inline check (coordination_objects.py:735): 400, statusChange only
        //     ("TeeUp is {status}; no further stage change" / "Cannot cancel a TeeUp that is already
        //     {status}") -- already covered pre-fix, kept as-is (400, not 409).
        // Detection is by DETAIL TEXT (cancel/ended), not status code alone, since 409 also covers
        // ordinary non-terminal conflicts this backstop must not swallow.
        const detail = (e && e.detail || "").toLowerCase()
        const isTerminalGuardRejection =
          e &&
          e.status !== 422 &&
          (e.status === 409 || (event === "statusChange" && e.status === 400)) &&
          (detail.indexOf("cancel") !== -1 || detail.indexOf("ended") !== -1)
        if (isTerminalGuardRejection) {
          let message = e.detail || msg
          if (detail.indexOf("cancel") !== -1) {
            message = I18n.t("isAlreadyCancelledWarning")
          } else if (detail.indexOf("ended") !== -1) {
            message = I18n.t("isAlreadyEndedWarning")
          }
          confirmAlert({
            message,
            buttons: [{ label: "Okay", onClick: () => {} }],
          })
        }
        // eslint-disable-next-line no-console
        console.warn("[devSocket emit]", event, msg)
      }
    },
  }
  // close the realtime socket when the shim socket is torn down
  socket.close = () => {
    try {
      if (rs) rs.close()
    } catch (e) {
      /* ignore */
    }
    return socket
  }
  socket.disconnect = socket.close
  return socket
}

const USER_STATUS_NAME = {
  1: "invited",
  2: "going",
  3: "might_go",
  4: "not_going",
  5: "joined",
  6: "on_my_way",
  7: "arrived",
}

// Shim for the inbox-level (teeupList) socket — handles the participant status writes ("I'm going").
export const makeDevListSocket = ({ token, userId }) => {
  const handlers = {}
  const fire = (event, ...args) =>
    (handlers[event] || []).forEach(h => {
      try {
        h(...args)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[devListSocket]", event, e)
      }
    })

  // chain-3 U-N3a-2: this was a PURE STUB -- no live connection at all, so every
  // teeupListSocket.on(...) listener registered in setupTeeupListSocket (teeup:invitation,
  // teeup:message, teeup_update, decide, suggestion, teeup:statusUpdate, profile:update,
  // user:createPassword) was dead code, never invoked by anything. Mirrors the proven `rs`
  // pattern in makeDevTeeupSocket above: opens a REAL connection to the realtime relay with NO
  // teeup_id (matches the beta's real setupTeeupListSocket shape -- query {token, user_id} only),
  // which realtime/server.js joins to `user:{userId}`, the "Teeup List" user-room chain-3 U-N2
  // built server-side.
  let rs = null
  try {
    const SOCKET_URL = process.env.REACT_APP_SOCKET_ENDPOINT
    if (SOCKET_URL) {
      // eslint-disable-next-line global-require
      const io = require("socket.io-client")
      rs = io.connect(SOCKET_URL, {
        transports: ["websocket"],
        forceNew: true,
        query: { token, user_id: userId },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      })
      // Real relay pushes confirmed live (realtime/server.js): invite -> teeup:new +
      // teeup:invitation to the invitee's user room; teeup:kickUser / teeup:leave -> teeup:skip
      // to the removed user's own user room; teeup:archive -> teeup:archive to the acting user's
      // own user room. "teeup_update", "profile:update", "user:createPassword" have NO server-side
      // emit anywhere in realtime/server.js (independently grepped, zero matches) -- a real,
      // pre-existing beta-parity gap that needs its own backend unit, named here per the
      // never-silently-drop rule, NOT built. Their listeners stay registered (harmless) but remain
      // unreachable until that backend work exists.
      ;["teeup:new", "teeup:invitation", "teeup:skip", "teeup:archive"].forEach(evt =>
        rs.on(evt, (...args) => fire(evt, ...args))
      )
      rs.on("connect_error", e =>
        // eslint-disable-next-line no-console
        console.warn("[devListSocket] realtime connect_error", e && e.message)
      )
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[devListSocket] realtime init failed", e && e.message)
  }

  const socket = {
    connected: true,
    io: { on: () => {}, off: () => {} },
    on: (e, cb) => {
      ;(handlers[e] = handlers[e] || []).push(cb)
      return socket
    },
    once: (e, cb) => socket.on(e, cb),
    off: () => socket,
    removeListener: () => socket,
    removeAllListeners: () => socket,
    open: () => socket,
    connect: () => socket,
    close: () => {
      if (rs) rs.close()
      return socket
    },
    disconnect: () => socket.close(),
    emit: async (event, payload = {}, meta = {}, ack) => {
      try {
        if (event === "teeup:participant:status") {
          const tid = meta.teeupId || payload.teeupId
          const statusName =
            USER_STATUS_NAME[payload.status] ||
            USER_STATUS_NAME[payload.userStatus] ||
            "going"
          await fetch(
            `${API_BASE}/v1/coordination-objects/${unaliasId(
              tid
            )}/participants/me/status?token=${encodeURIComponent(token)}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ status: statusName, status_context: null }),
            }
          ).catch(() => {})
          // chain-3 U-N3a-2: opportunistic fix, same emit call, same scope -- joinTeeup() passes a
          // 4th ack callback (used to optimistically update the inbox store's own userstatus) that
          // this stub silently swallowed (only 3 params were ever read). Real bug, one line, fixed
          // in place rather than filed separately.
          if (typeof ack === "function") ack()
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[devListSocket emit]", event, e && e.message)
      }
    },
  }
  return socket
}

// ---- dev login -> {user, token}. The dev backend resolves the identifier by
// username | legacy_id | email (any of the user's real emails, from public.user_emails populated
// from the migrated legacy data) — so any migrated user signs in with their real email (or the
// userId claim, passed by the Auth0 bridge). No hardcoded mapping. ----
export const devLogin = async username => {
  const res = await fetch(`${API_BASE}/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: String(username || "").trim() }),
  })
  if (!res.ok) {
    let detail = `${res.status}`
    try {
      detail = (await res.json())?.detail || detail
    } catch {
      /* non-json */
    }
    const err = new Error(detail)
    err.status = res.status
    throw err
  }
  const data = await res.json() // { user, token }
  const u = data.user || {}
  // synthesize the beta login response shape saveUserAndRedirect/createSession expects
  return {
    accessToken: data.token,
    refreshToken: data.token,
    loginType: "email",
    user: {
      id: aliasId(u.id),
      userId: aliasId(u.id),
      hasPassword: true,
      name: u.display_name,
      username: u.display_name,
      email: u.email || "",
      avatar: u.avatar_url || "",
      timezone: u.timezone || "UTC",
    },
  }
}

// ---- WB4 (Wave 3): Leave a TeeUp -- DELETE /v1/coordination-objects/{id}/participants/me. ----
// The web "Leave the Teeup" settings button existed but its handler (dropoutTeeup in
// backendMiddleware.js) was entirely commented out except for a socket emit the dev deploy's
// socket shim never handled -- so clicking Leave was a silent no-op (route already existed on the
// real backend, confirmed live: participants.py withdraw()). This calls it directly.
export const leaveTeeupDev = async teeupId => {
  const token = getToken()
  const uuid = unaliasId(teeupId)
  const res = await fetch(
    `${API_BASE}/v1/coordination-objects/${uuid}/participants/me?token=${encodeURIComponent(
      token
    )}`,
    { method: "DELETE" }
  )
  if (!res.ok) {
    let detail = `${res.status}`
    try {
      detail = (await res.json())?.detail || detail
    } catch {
      /* non-json */
    }
    const err = new Error(detail)
    err.status = res.status
    throw err
  }
  return res.json()
}

// SHARED-HISTORY (2026-07-04): real, paginated, ALL-TIME/ALL-STATUS shared-CO history between the
// signed-in viewer and a given user id, off the new dev backend
// GET /v1/users/{user_id}/shared-coordination-objects (see api/routers/auth.py docstring for the
// full contract + the count-parity note vs GET /directory). `userId` here is the RAW uuid off
// GET /users/{id} (the profile route's own target user id shape) -- no aliasing needed, this is a
// brand-new dev-only path with no beta counterpart to translate. Returns the dev response as-is
// ({items, total_count, limit, offset}); the caller (useProfilePageSharedTeeUps) reshapes each
// item into the TeeupItem-compatible shape. Throws on a non-OK response so the caller can fall
// back to the client-side derivation (the pre-existing, Redux-only-derived list) rather than
// silently showing an empty tab.
export const fetchSharedCoordinationObjects = async (userId, { limit = 50, offset = 0 } = {}) => {
  const token = getToken()
  const res = await fetch(
    `${API_BASE}/v1/users/${encodeURIComponent(
      userId
    )}/shared-coordination-objects?token=${encodeURIComponent(
      token
    )}&limit=${limit}&offset=${offset}`,
    { headers: { "Content-Type": "application/json" } }
  )
  if (!res.ok) {
    const err = new Error(`shared-coordination-objects ${res.status}`)
    err.status = res.status
    throw err
  }
  return res.json()
}

// T-W6-F1 (INGESTION W6, 2026-07-06): multi-person intersection -- coordination objects shared by
// the viewer AND every selected person (the People multi-select "View shared history" action, per
// deterministic-interaction-ingestion-plan-2026-07-04.md §4-TASKS T-W6-B1/T-W6-F1). T-W6-B1 specs
// a back-compatible `with=` param on this SAME route (GROUP/HAVING cardinality over
// interaction_events, channel-coverage-labeled response) -- it is a sibling backend track and its
// live response shape is not confirmed from here yet, so this helper tries the `with=` param
// first but only TRUSTS it if the response proves it actually understood the param (an explicit
// `with_ids` echo covering every requested id) -- an OLDER server would silently ignore an
// unrecognized query param and return a plain PAIRWISE result for `firstId` alone, which would be
// wrong to present as a 3-way intersection. Falls back to a client-side intersection that is
// PROVABLY correct against the endpoint as already deployed today: fetch the viewer's shared
// history with each selected person separately (paginating to completion) and keep only the
// coordination objects present in every one of those sets -- a CO in the (viewer, A) set AND the
// (viewer, B) set necessarily has the viewer, A, and B all as participants, since each pairwise
// fetch already filters to COs shared with the viewer.
const fetchAllSharedCoordinationObjects = async userId => {
  let all = []
  let offset = 0
  const pageLimit = 200
  const hardCap = 5000 // safety valve; no real user is expected to approach this
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const data = await fetchSharedCoordinationObjects(userId, { limit: pageLimit, offset })
    const items = data?.items || []
    all = all.concat(items)
    offset += items.length
    const total = typeof data?.total_count === "number" ? data.total_count : all.length
    if (items.length === 0 || offset >= total || offset >= hardCap) break
  }
  return all
}

const fetchSharedCoordinationObjectsIntersectionClientSide = async (userIds, { limit, offset }) => {
  const perPerson = await Promise.all(userIds.map(fetchAllSharedCoordinationObjects))
  const [first, ...rest] = perPerson
  const restIdSets = rest.map(list => new Set(list.map(i => i.id)))
  const intersected = first.filter(item => restIdSets.every(idSet => idSet.has(item.id)))
  intersected.sort(
    (a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0)
  )
  return {
    items: intersected.slice(offset, offset + limit),
    total_count: intersected.length,
    // Channel-coverage label (T-W6-B1 contract): only TeeUps are wired today; meetings/emails
    // arrive as W3/W4 land. Labeled honestly, never fabricated.
    coverage: { teeups: true, meetings: false, emails: false },
    client_intersection: true,
  }
}

export const fetchSharedCoordinationObjectsForMany = async (
  userIds,
  { limit = 50, offset = 0 } = {}
) => {
  const ids = (userIds || []).filter(Boolean)
  if (ids.length === 0) return { items: [], total_count: 0, coverage: { teeups: true } }
  if (ids.length === 1) {
    const data = await fetchSharedCoordinationObjects(ids[0], { limit, offset })
    return { ...data, coverage: { teeups: true, meetings: false, emails: false } }
  }
  const [firstId, ...restIds] = ids
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/${encodeURIComponent(firstId)}/shared-coordination-objects?with=${restIds
        .map(encodeURIComponent)
        .join(",")}&token=${encodeURIComponent(token)}&limit=${limit}&offset=${offset}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (res.ok) {
      const data = await res.json()
      const withIds = Array.isArray(data?.with_ids) ? data.with_ids.map(String) : null
      const understood = withIds && restIds.every(id => withIds.includes(String(id)))
      if (understood) {
        return { ...data, coverage: data.coverage || { teeups: true, meetings: false, emails: false } }
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[intersection] with= endpoint not usable yet, using client-side intersection", e)
  }
  return fetchSharedCoordinationObjectsIntersectionClientSide(ids, { limit, offset })
}

// AVATAR-RENDER (2026-07-04): server-side uniform-padding trim lookup for a given avatar image
// URL, off the new dev backend GET /media/avatar-crop (see api/routers/media.py's module
// docstring for the full CORS/why-server-side explanation). Returns
// {trimmed:false} when nothing is fetchable/decodable or no padding was found -- never throws (the
// caller, PeopleAvatar, always has its own initials fallback for that case). A no-op on non-dev
// builds (isDevBackend() false): resolves {trimmed:false} immediately, no network call.
export const fetchAvatarCrop = async avatarUrl => {
  if (!isDevBackend() || !avatarUrl) return { trimmed: false }
  try {
    const res = await fetch(
      `${API_BASE}/v1/media/avatar-crop?url=${encodeURIComponent(avatarUrl)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { trimmed: false }
    return await res.json()
  } catch {
    return { trimmed: false }
  }
}

// PERSON-CARD (T2, 2026-07-06, rewired 2026-07-06 4-EXP): merged, mined contact mechanisms for a
// counterpart -- the "from your history" private layer per
// deterministic-interaction-ingestion-plan-2026-07-04.md §4-PROVENANCE (assertion_class:
// self_published | shared_with_viewer | viewer_private) and §0-GOAL (per-channel CURRENT-BEST
// mechanism, provenance-chipped, merged across email/mobile/etc).
//
// REAL, LIVE CONTRACT (confirmed live on coowe-api-dev, contact-mechanism-harvest track,
// api/routers/contacts.py `contact_mechanisms`):
//   GET /v1/users/me/contacts/mechanisms?counterpart_user_id=<id>|counterpart_key=<key>&token=
//   -> { counterpart_key, channels: { <channel>: MechRow[] } }
//   MechRow = { value, direction, assertion_class, source, evidence_ref, first_seen, last_used,
//               use_count, is_current_best }
//   -- rows per channel arrive PRE-SORTED (is_current_best DESC, last_used DESC, use_count DESC).
// This is a VIEWER-SCOPED "me" route (not /users/{id}) -- the mined mechanisms are always the
// signed-in viewer's OWN mined history about the counterpart, never the counterpart's own data.
//
// Reshaped here into the shape PersonCard.js already renders (never changing that component):
// one current-best MechRow per channel in `channels`, plus any OTHER distinct value for that
// channel surfaced as a `conflicts[]` entry (never silently dropped, never silently merged --
// same "both layers render, no silently-picked winner" rule the two-layer contract already
// applies between "their profile" and "from your history"). `seen_at` is fed from the real
// `last_used` field (never fabricated when absent).
// Never throws -- 404/network/parse errors all resolve { ok:false }, honest empty state.
// ID-SPACE FIX (2026-07-06, 4-EXP): callers here (PersonCard.js's openPersonId off the People
// Directory row, ProfilePage's useParams `id`) are in the FE's own INT-ALIAS space (see the
// aliasId/unaliasId doc block above -- devDirUser() emits aliasId(u.id) as `userId`/`id`, and
// history.push(`/profile/${r.userId}`) carries that same alias into the route param). This
// endpoint's `counterpart_user_id` is a REAL dev-backend UUID -- passing the alias straight
// through silently 200s with `channels:{}` (confirmed live: no error, just an empty merged-mined
// layer, which is exactly the "card looks empty but the data is really there" symptom). unaliasId
// is a no-op passthrough for anything that already looks like a UUID (or isn't a known alias), so
// this is safe for any future caller that already passes a real UUID.
export const fetchPersonMechanismsMerged = async userId => {
  if (!isDevBackend() || !userId) return { ok: false }
  try {
    const token = getToken()
    const realUserId = unaliasId(userId)
    const res = await fetch(
      `${API_BASE}/v1/users/me/contacts/mechanisms?counterpart_user_id=${encodeURIComponent(
        realUserId
      )}&token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const raw = await res.json()
    const rawChannels = raw?.channels || {}

    const toMechRow = row => ({
      value: row.value,
      assertion_class: row.assertion_class,
      current_best: !!row.is_current_best,
      source: row.source,
      evidence_ref: row.evidence_ref,
      seen_at: row.last_used || row.first_seen || null,
      // DECLUTTER (QJ 2026-07-06): carry the REAL last_used through so the profile can show a
      // simple "last used <date>" ONLY when the address was actually used (never on a first_seen
      // fallback, which would misrepresent a never-used registered address as "last used").
      last_used: row.last_used || null,
    })

    const channels = {}
    const conflicts = []
    Object.entries(rawChannels).forEach(([channel, rows]) => {
      if (!Array.isArray(rows) || rows.length === 0) return
      const [best, ...rest] = rows
      channels[channel] = toMechRow(best)
      const seenValues = new Set([best.value])
      rest.forEach(row => {
        // only a genuinely different value is a "conflict" -- repeated harvest rows for the
        // SAME value (duplicate evidence) are not surfaced as a conflicting mechanism.
        if (seenValues.has(row.value)) return
        seenValues.add(row.value)
        conflicts.push({ channel, ...toMechRow(row) })
      })
    })

    return { ok: true, data: { channels, conflicts } }
  } catch {
    return { ok: false }
  }
}

// PERSON-CARD / TIMELINE (T-W3-F2, rewired 2026-07-06 4-EXP): meeting/email event rows for the
// profile Timeline tab, beyond the TeeUps already served by fetchSharedCoordinationObjects.
//
// REAL, LIVE CONTRACT (confirmed live on coowe-api-dev, person-card-endpoints track,
// api/routers/interaction_events.py `my_interaction_events`):
//   GET /v1/users/me/interaction-events?counterpart_user_id=<id>|counterpart_key=<key>
//       &channel=email,meeting&limit=&offset=&token=
//   -> { items: [{ id, occurred_at, channel, direction, thread_or_event_key, duration_min,
//                  title }], total_count }
// Viewer-scoped "me" route (the signer's own interaction_events with this one counterpart).
// channel=email,meeting is passed explicitly here (not the full 4-channel set) so this NEW event
// layer never duplicates the coowe_teeup/coowe_message rows the Timeline already merges in via
// the existing sharedTeeUps fetch -- see useProfilePageTimeline.js's merge.
// `title` is honestly null on every email/meeting row today (this schema has no Subject/title
// for those channels yet -- gmail_ingest is metadata-scope-only); ProfilePageTimeline.js already
// renders "(untitled)" for that case, never a fabricated title. `location`/`modality` are not on
// the real row (no calendar_events table has landed) -- left absent, matching the real-empty-
// state law; the renderer already guards on `event.location &&`.
// Never throws -- 404/network/parse errors all resolve { ok:false }, honest empty state.
export const fetchPersonInteractionEvents = async (userId, { limit = 50, offset = 0 } = {}) => {
  if (!isDevBackend() || !userId) return { ok: false }
  try {
    const token = getToken()
    // ID-SPACE FIX (2026-07-06, 4-EXP): same alias/UUID mismatch as fetchPersonMechanismsMerged
    // above -- unalias before hitting the real backend.
    const realUserId = unaliasId(userId)
    const res = await fetch(
      `${API_BASE}/v1/users/me/interaction-events?counterpart_user_id=${encodeURIComponent(
        realUserId
      )}&channel=email,meeting&limit=${limit}&offset=${offset}&token=${encodeURIComponent(
        token
      )}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const raw = await res.json()
    const items = (raw?.items || []).map(item => ({
      id: item.id,
      channel: item.channel,
      occurred_at: item.occurred_at,
      title: item.title || null,
      duration_min: item.duration_min,
    }))
    return {
      ok: true,
      data: { items, total_count: typeof raw?.total_count === "number" ? raw.total_count : items.length },
    }
  } catch {
    return { ok: false }
  }
}

// STANCE (4-EXP, O4 coordination-stance layer, profile Stance tab, 2026-07-06): read-only
// deterministic coordination stance for one counterpart -- preferred meeting times, preferred
// methods/modality, cadence, responsiveness, typical purposes -- each carrying its own
// `based_on_events` coverage count. Real, live contract (api/routers/stance.py `my_contact_stance`
// on coowe-api-dev, createbrain-canonical-person track):
//   GET /v1/users/me/contacts/stance?counterpart_user_id=<id>|counterpart_key=<key>&token=
//   -> { has_data:false, coverage_note, based_on_events } when there is too little mined history
//      (real-empty-state law: NEVER a fabricated preferred time/method/cadence/purpose), else
//   -> { has_data:true, based_on_events, preferred_times, preferred_methods, cadence,
//        responsiveness, typical_purposes } -- each field pre-shaped server-side (ranked lists,
//        weekday/hour histograms, human `summary` strings), rendered as-is, no client-side
//        inference. Never throws -- 404/network/parse errors resolve { ok:false }, honest empty.
// PERSON TAGS (O3/O4, QJ 2026-07-07): deterministic "what we have worked on or connected
// about" tags for one counterpart, rendered as chips at the top of the profile. Real, live
// contract (api/routers/person_tags.py `my_contact_tags` on coowe-api-dev, person-tags track):
//   GET /v1/users/me/contacts/tags?counterpart_user_id=<id>&token=
//   -> { work_units: [{ tag, weight, based_on_count, roster:[{key,name,n}], sources,
//        first_seen, last_seen }], tags: [{ tag, tag_type, weight, based_on_count, sources }],
//        based_on_tag_rows }
// work_units = structured shared workflows (recurring meeting series / TeeUp series / shared
// Slack channels, each with its roster) -- rendered visually distinct from context chips.
// tags = context chips (topic / affiliation / relationship_class), weight-ordered, server-capped.
// A person with no derivable tags gets { work_units: [], tags: [] } -- true empty, the FE
// renders NOTHING (real-empty-state law; never a fabricated chip).
// Never throws -- 404/network/parse errors resolve { ok:false }, honest empty.
export const fetchPersonTags = async userId => {
  if (!isDevBackend() || !userId) return { ok: false }
  try {
    const token = getToken()
    const realUserId = unaliasId(userId)
    const res = await fetch(
      `${API_BASE}/v1/users/me/contacts/tags?counterpart_user_id=${encodeURIComponent(
        realUserId
      )}&token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return {
      ok: true,
      data: {
        workUnits: Array.isArray(data?.work_units) ? data.work_units : [],
        tags: Array.isArray(data?.tags) ? data.tags : [],
      },
    }
  } catch {
    return { ok: false }
  }
}

export const fetchPersonStance = async userId => {
  if (!isDevBackend() || !userId) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/me/contacts/stance?counterpart_user_id=${encodeURIComponent(
        userId
      )}&token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// A7 (invite-to-an-existing-TeeUp suggestions, QJ 2026-07-07): ranked candidates NOT already
// participants of this TeeUp, each carrying a `score` and a `why` evidence list (see
// api/routers/invite_suggestions.py's module docstring for the 4-signal derivation). Same
// honest-empty convention as fetchPersonStance -- a non-2xx or network error returns
// {ok:false}, never a fabricated list.
export const fetchInviteSuggestions = async coordinationObjectId => {
  if (!isDevBackend() || !coordinationObjectId) return { ok: false }
  try {
    const token = getToken()
    const uuid = unaliasId(coordinationObjectId)
    const res = await fetch(
      `${API_BASE}/v1/coordination-objects/${uuid}/invite-suggestions?token=${encodeURIComponent(
        token
      )}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// A8 (proactive suggestions v1, QJ 2026-07-07): "who to meet with about what; who belongs
// together". GET /users/me/suggestions (api/routers/proactive_suggestions.py) -- deterministic,
// capped(10), recency-weighted reconnect-overdue + gather rows, each carrying a remakeDraft
// create-prefill payload. Same honest-empty {ok:false} convention as fetchInviteSuggestions.
export const fetchProactiveSuggestions = async () => {
  if (!isDevBackend()) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/me/suggestions?token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// CONNECT-AND-CLAIM (plan 4-EXP, 2026-07-07): the logged-in user's own verified claims
// (api/routers/identity.py, migrations/2026-07-07-verified-claims-merge.sql). Zero-effort
// framing: a Google/Auth0 login already auto-claims that login email server-side (see
// api/routers/auth.py login()) -- this surface shows what's already connected and offers
// "add another Google account" / "add an email or phone" for anything not yet claimed.
export const fetchVerifiedClaims = async () => {
  if (!isDevBackend()) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/me/verified-claims?token=${encodeURIComponent(token)}`,
      { headers: { "Content-Type": "application/json" } }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data: data.claims || [] }
  } catch {
    return { ok: false }
  }
}

// EA query (QJ 2026-07-07): fetch from /v1/ea/query with question_type and params as JSON body
export const fetchEaQuery = async (questionType, params) => {
  if (!isDevBackend() || !questionType) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/ea/query?token=${encodeURIComponent(token)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_type: questionType, params }),
      }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// Agent plan creation (QJ 2026-07-07): POST to /v1/coordination-objects/agent-plan with
// participant_user_ids and purpose as JSON body
export const postAgentPlan = async (participantUserIds, purpose) => {
  if (!isDevBackend() || !participantUserIds) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/coordination-objects/agent-plan?token=${encodeURIComponent(token)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participant_user_ids: participantUserIds, purpose }),
      }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// AI suggestions for gameplan (QJ 2026-07-07): POST to
// /v1/coordination-objects/{id}/gameplan/ai-suggest with dimension and timezone as JSON body
export const fetchGameplanAiSuggest = async (coordinationObjectId, dimension) => {
  if (!isDevBackend() || !coordinationObjectId || !dimension) return { ok: false }
  try {
    const token = getToken()
    const uuid = unaliasId(coordinationObjectId)
    const res = await fetch(
      `${API_BASE}/v1/coordination-objects/${uuid}/gameplan/ai-suggest?token=${encodeURIComponent(
        token
      )}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dimension, timezone: (Intl.DateTimeFormat().resolvedOptions().timeZone || "") }),
      }
    )
    if (!res.ok) return { ok: false, status: res.status }
    const data = await res.json()
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

// method: 'code' (OTP/magic-link path) is the only one this UI drives directly today -- the
// actual send is QJ-gated (server returns send_gated:true, no email/SMS goes out). 'google_oauth'
// claims land automatically at login; a UI-driven "connect another Google account" button would
// call this same endpoint with method:'google_oauth' once a second Auth0/Google identity exchange
// is wired client-side (out of scope for this pass -- the endpoint contract is ready for it).
export const addVerifiedClaim = async (channel, value, method = "code") => {
  if (!isDevBackend()) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/me/verified-claims?token=${encodeURIComponent(token)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel, value, method }),
      }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return { ok: false, status: res.status, error: data?.error || data?.detail }
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}

export const verifyClaimCode = async (claimId, code) => {
  if (!isDevBackend() || !claimId) return { ok: false }
  try {
    const token = getToken()
    const res = await fetch(
      `${API_BASE}/v1/users/me/verified-claims/${encodeURIComponent(
        claimId
      )}/verify-code?token=${encodeURIComponent(token)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return { ok: false, status: res.status, error: data?.error || data?.detail }
    return { ok: true, data }
  } catch {
    return { ok: false }
  }
}
